"use client"

import type React from "react"

import Link from "next/link"
import { PublicNavGlass } from "@/components/public-nav-glass"
import { Trash2, Lock, MessageSquare, Lightbulb, Handshake, Flag, Mic, Users, ChevronDown } from "lucide-react"
import { useState, useEffect } from "react"
import { useLocale, useTranslations } from "next-intl"
import { PulseDot } from "@/components/ui/pulse-dot"

// Localize display of data dates ("June 7, 2026" etc.) without touching the
// underlying entry data. Falls back to the raw string if it doesn't parse.
function formatEntryDate(dateStr: string, locale: string): string {
  const parsed = new Date(dateStr)
  if (Number.isNaN(parsed.getTime())) return dateStr
  return new Intl.DateTimeFormat(locale, { year: "numeric", month: "long", day: "numeric" }).format(parsed)
}

/**
 * FOUNDER'S JOURNAL - How to Add New Entries
 *
 * To add a new journal entry, copy the structure below and paste it at the TOP of the journalEntries array:
 *
 * {
 *   date: "March 30, 2026",
 *   title: "Entry Title",
 *   body: "Your journal entry text here...",
 *   tags: ["conversation", "partnerships"]  // Options: "conversation", "idea", "milestone", "lesson", "event", "community"
 * }
 */

const journalEntries = [
  {
    date: "March 30, 2026",
    title: "A Phone Call That Mattered",
    body: `Prepping for the big day — April 1st. It's a beautiful day, 70s in Chicago. A week ago it was 30 degrees and snowing. Chicago weather.

Anyway, we're stress testing the BOOTH web app today — optimizing the platform and squashing bugs on the map and a few other pages. The grind doesn't stop.

Then we received a DM from Joe Flano, a prominent figure in the barbershop industry here in the city. He tapped in with us asking if we'd be open to showcasing BOOTH at the Midwest Barber Trade Show in Chicago on July 12th. To me, this was a no-brainer. We DM'd back and forth, and while I was at Drip Collective in Fulton Market, I gave him a call around 2 PM. The call lasted about 30 minutes.

We spoke about the product, my vision, and what I know will happen. I also shared my vision for BOOTH inside the Midwest Barber Trade Show — how we want a simple setup: a banner, a table with a tablecloth with the BOOTH logo, flyers, and cool swag. Hoping we can get funding soon to make that happen.

I told him this is my magnum opus. My life's work.

He gave me words of wisdom and inspiration, and spoke about how he saw the founders of Squire and theCut in the same stages we're in with BOOTH right now. He mentioned his close relationship with them and how seeds were planted early on. I told him that's exactly what I want to do with people like him — plant seeds for a fruitful, symbiotic relationship.

I especially valued the fact that he thinks it's a great idea, and how he kept saying "keep on sharing the good news." Conversations like these matter so much.`,
    tags: ["conversation", "partnerships", "event"],
  },
  {
    date: "March 27, 2026",
    title: "Putting the Story Out There",
    body: `We finished refining our founder story and projected it into the digital world — promoted it across Instagram, Twitter, Threads, LinkedIn, and Facebook. So many friends and family showed their love and support. It fills me with pride knowing they have my back. That they believe in me.

My story is a unique one. It blends my history as a barber, designer, and entrepreneur — three paths now fusing together as I work toward something that can complete my journey in this industry. It's an exciting time.

We also posted photos from a photoshoot we had at BOOTH HQ in Pilsen. One image we highlighted was me on the roof, overlooking Chicago, holding my laptop. That laptop is the tool that has enabled me to build this entire project. It's my prized possession and the ultimate instrument for me at this moment in time. I can't imagine not having it to build with in a time like this.

So grateful to have this opportunity — to follow my dreams, to make other people's dreams possible, and to be surrounded by an overwhelming amount of support from my people. It's inspiring. We're here for it. Let's do this.`,
    tags: ["milestone", "community"],
  },
]

const tagConfig: Record<string, { icon: typeof MessageSquare; label: string; color: string }> = {
  conversation: { icon: MessageSquare, label: "Conversation", color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  idea: { icon: Lightbulb, label: "Idea", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  partnerships: { icon: Handshake, label: "Partnerships", color: "text-purple-400 bg-purple-500/10 border-purple-500/20" },
  milestone: { icon: Flag, label: "Milestone", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
  event: { icon: Mic, label: "Event", color: "text-pink-400 bg-pink-500/10 border-pink-500/20" },
  lesson: { icon: Lightbulb, label: "Lesson", color: "text-orange-400 bg-orange-500/10 border-orange-500/20" },
  community: { icon: Users, label: "Community", color: "text-teal-400 bg-teal-500/10 border-teal-500/20" },
}

/**
 * CHANGELOG - How to Add New Entries
 *
 * To add a new update, copy the structure below and paste it at the TOP of the updates array:
 *
 * {
 *   date: "January 6, 2026",
 *   items: [
 *     {
 *       type: "new",      // Options: "new", "improved", "fixed", "removed"
 *       title: "Feature Name",
 *       description: "Brief description of what changed and why it matters."
 *     }
 *   ]
 * }
 *
 * Keep descriptions concise and user-focused. Group related changes under the same date.
 */

// The public changelog. Read docs/changelog.md before adding to it.
//
// Short version: this is a shopfront, not a release log. It says what the
// product does now and never what we found wrong with it — no security notes,
// no counted fixes of our own data, no internal tooling, nothing about how the
// sausage is made. The test is whether a shop owner reading an entry would
// think better or worse of BOOTH. If worse, it is a commit message.
const updates = [
  {
    date: "October 13, 2026",
    items: [
      {
        type: "improved",
        title: "The Page a Shop Owner Lands On",
        description:
          "The page an owner opens to claim their shop is often the first thing they see of BOOTH, so it now says what BOOTH is for: barbers can see which shops near them have chairs open, and owners never have to sit on a booth that isn\u2019t being rented. The BOOTH mark sits above the shop\u2019s own, \u201cSet your email and password and it\u2019s yours\u201d leads into the form rather than trailing the pitch, and the shop\u2019s mark is the first thing on the page to load.",
      },
      {
        type: "improved",
        title: "Sheets Open From the Bottom Edge",
        description:
          "Contact a shop on a wide screen and the sheet used to lift onto a floating card, away from the edge it had just risen from. It stays anchored to the bottom now, with only its top corners rounded, so the gesture it suggests is the one it actually makes. Every sheet on BOOTH opens this way.",
      },
    ],
  },
  {
    date: "October 2, 2026",
    items: [
      {
        type: "improved",
        title: "The Pricing Page Says Exactly Where the Money Goes",
        description:
          "Every sentence about fees was checked against what the code actually charges and what Stripe actually bills. Three things changed: card processing is described as paid out of BOOTH's 5% rather than vaguely absorbed, memberships are named alongside appointments because they were always charged the same way, and the page now says that services under $5 are collected in person. Shops still receive the full rent they list, and clients still pay exactly the price their barber sets.",
      },
      {
        type: "fixed",
        title: "The \u201cProcessing\u201d Line Is Gone",
        description:
          "Barbers could switch on a line that added card-processing cost to the client's total. It was collected from the client and then paid to the barber, while BOOTH paid the card fee either way, so the line covered nobody's cost. It is off for new bookings. Appointments booked before this keep the totals they were booked at, and their receipts are unchanged.",
      },
      {
        type: "fixed",
        title: "A Settings Switch That Did Nothing",
        description:
          "Account settings still offered \u201cPass processing cost to client\u201d after that option was withdrawn, and described a line clients would never see. It now says plainly that BOOTH covers card processing out of its fee.",
      },
      {
        type: "improved",
        title: "Small Appointments Are Collected at the Chair",
        description:
          "BOOTH's fee has a $1 minimum, which on a $3 service would have taken a third of it. Services under $5 no longer take card payment online: the booking goes through as normal and the barber collects in person, the same way it works with deposits switched off.",
      },
    ],
  },
  {
    date: "October 1, 2026",
    items: [
      {
        type: "new",
        title: "Guest Passes",
        description:
          "A shop can now offer a guest pass: a short rate for a barber passing through, rather than one joining the floor for good. It sits with the other booth options on a listing as an inverted card, so it reads as a different kind of offer instead of a fourth rate to compare. Owners turn it on per plan from Booth Plans or while creating a shop, and the plan still carries its own term, so a guest pass can run by the day, the week or the month.",
      },
      {
        type: "fixed",
        title: "Two Sign Up Buttons in One Corner",
        description:
          "The job board and the role pages were showing a Sign Up in the corner on top of the Join button already in the navigation bar. One of them now, where it belongs.",
      },
      {
        type: "fixed",
        title: "The Map Lines Up With the Listings",
        description:
          "On a computer the map sat higher than the roles beside it and ran up behind the navigation bar, which covered the count of how many listings were on screen. Both columns start on the same line now.",
      },
      {
        type: "improved",
        title: "The Job Board Shows More of the Map",
        description:
          "Listings and the map now split the screen evenly on a computer, instead of the map getting a third of it. The board also opens in the compact row view, which puts far more roles in front of you at once, and the grid is still a click away. Phones are unchanged.",
      },
      {
        type: "improved",
        title: "One Set of Filters",
        description:
          "City and Filters on the job board are the same control now. Choosing a city used to open your phone or computer's own menu, which looked nothing like the button beside it and ignored dark mode. Both are BOOTH's own, and a chosen city fills in so you can see at a glance that something is narrowing the board.",
      },
      {
        type: "fixed",
        title: "Descriptions Keep Their Shape",
        description:
          "A shop description written with headings, lists and paragraphs was being flattened into one block of text on the listing page, no matter how the owner typed it. The line breaks now survive, on the public listing and in the owner's preview.",
      },
      {
        type: "fixed",
        title: "The Map Stops Where You Stop It",
        description:
          "Dragging the map's handle on Browse Shops used to snap to one of three fixed heights when you let go, so almost every drag ended somewhere you had not chosen. It stays where you put it.",
      },
{
        type: "fixed",
        title: "Job Posts Stay Up",
        description:
          "Postings used to come down on their own after sixty days, with no notice to the shop that wrote them. A posting now stays live until it is filled, unpublished or deleted. Seven postings that had quietly dropped off the board are back.",
      },
    ],
  },
  {
    date: "September 30, 2026",
    items: [
      {
        type: "new",
        title: "Barber Booth Rental in Every City",
        description:
          "Every city with a shop on BOOTH now has its own page in search results: the shops with chairs open, how many, and the rent where shops publish it. A city gets its page the day its first shop goes live.",
      },
      {
        type: "improved",
        title: "Easier to Find in Search and AI Assistants",
        description:
          "Shop listings, job posts, barber profiles and help guides now tell search engines and AI assistants exactly what they are: a shop with chairs for rent, a job in a city, a guide with steps. Listings and profiles also load fully for tools that read a page without running it, so when someone asks where to rent a barber chair, BOOTH has a real answer to give.",
      },
{
        type: "improved",
        title: "The Map Stays Where You Put It",
        description:
          "Dragging the map taller or shorter used to snap it to one of three fixed sizes, so it almost never ended where you let go. It now keeps the exact height you drag it to, on a phone and on a computer.",
      },
      {
        type: "improved",
        title: "Shop Descriptions Keep Their Formatting",
        description:
          "Line breaks, lists and paragraphs an owner types into their shop description now show up on the listing. Before, all of it ran together into one block of text, and the owner's preview shows exactly what visitors see.",
      },
      {
        type: "improved",
        title: "A Barber Profile Built Around Booking",
        description:
          "Instagram and TikTok posts on a barber's profile move to the end, after Shop Experience. Booking, memberships, portfolio and hours now come right after the intro instead of below a tall embed.",
      },
      {
        type: "improved",
        title: "Cleaner Place Names",
        description:
          "Cities and states display the same way everywhere, whatever was typed: “Chicago, IL”, not “Chicago , IL” or “chicago, illinois”. Page titles for shops and barbers now say what the page is, like “Elite Barber Lounge: Barber Booth Rental in Chicago, IL”.",
      },
      {
        type: "new",
        title: "Brands Can Host Offers on BOOTH",
        description:
          "BOOTH has opened a call for brands to host their coupons and offers on the platform. Barbers earn points on every rent payment through BOOTH, and partner offers are where those points go.",
      },
    ],
  },
  {
    date: "September 29, 2026",
    items: [
      {
        type: "improved",
        title: "Job Posts Stay Up Until You Fill Them",
        description:
          "Openings no longer expire on their own after 60 days. A post stays live until the owner marks it filled, unpublishes it or deletes it, so the Expired badge, countdown and Renew button are gone.",
      },
      {
        type: "new",
        title: "Nearby Shops Group on the Map",
        description:
          "When shops sit close together, the map shows one pin with a count instead of a pile of overlapping ones. Tap it to zoom in, and at street level the pins fan out. Choosing a shop from the list zooms until its pin is actually visible.",
      },
      {
        type: "improved",
        title: "Where a Job Was First Posted",
        description:
          "If an owner also announced an opening on Instagram, Facebook, TikTok, X, Threads, YouTube, Snapchat or Indeed, the job post links to it with that platform's mark, so barbers can see the original.",
      },
      {
        type: "improved",
        title: "A Simpler Loading Bar",
        description:
          "Loading screens now show the same thin green bar you see when signing in instead of a counter ticking from 0 to 99, and signing in goes straight to your dashboard without the extra welcome screen.",
      },
    ],
  },
  {
    date: "September 28, 2026",
    items: [
      {
        type: "improved",
        title: "Inquire on Every Booth Option",
        description:
          "The button on a shop's booth options now reads Inquire and stays on one line, instead of a Contact Shop pill that broke across two lines on narrow cards.",
      },
    ],
  },
  {
    date: "September 27, 2026",
    items: [
      {
        type: "improved",
        title: "Your Shops in One Row",
        description:
          "Owners with more than one shop now swipe through them in a single row on every screen size. On a computer, a third shop used to wrap onto a second row, half of it below the fold.",
      },
    ],
  },
  {
    date: "September 24, 2026",
    items: [
      {
        type: "improved",
        title: "Your Account Details Stay Yours",
        description:
          "Contact and billing details on an account — email, phone number, payment identifiers — are no longer readable by other signed-in accounts. What a barber or shop shares publicly is unchanged: name, photo, role, and bio still appear on profiles, rosters, and message threads, because that is what makes the directory work.",
      },
      {
        type: "new",
        title: "Browse Shops Goes International",
        description:
          "International is now a switch at the bottom of Filters. Off, Browse Shops stays on US cities; on, the country row comes back for every market BOOTH is in. Where you are decides which one you start on, and BOOTH remembers the switch if you flip it.",
      },
      {
        type: "improved",
        title: "The Filter Row on a Phone",
        description:
          "Shop types sit at the front of the row with cities beside them, so you can narrow by what kind of shop and where without opening anything. The full city list is still behind Filters. The Filters panel also stays on screen now instead of running past the right edge.",
      },
      {
        type: "new",
        title: "Your Location on the Map",
        description:
          "A blue pin marks where you are, and a button in the bottom-right corner of the map brings you back to it after you have been exploring. If you have used the map before, the pin is already down when the page opens.",
      },
      {
        type: "improved",
        title: "Search Points at the Shops",
        description:
          "On the search page, the button that used to say Learn more now says Explore Shops and opens the listings. Someone searching for work should land on the shops, not the marketing page. The headline reads “Find a barbershop to work at” — renting a chair is most of what is listed, and “hiring” left it out.",
      },
      {
        type: "fixed",
        title: "Quieter Status Alerts",
        description:
          "Our own uptime alerts were firing on any hiccup at Stripe or Resend, including incidents in countries and products BOOTH does not touch. They now watch only the services BOOTH actually runs on, and wait for a problem to persist before raising it.",
      },
    ],
  },
  {
    date: "September 21, 2026",
    items: [
      {
        type: "new",
        title: "Accept Offers in Messages",
        description:
          "A booth offer from a shop owner now lands in the conversation as a card the barber can accept or decline right there. The answer posts back into the thread, and the offer shows its live status on both sides.",
      },
      {
        type: "improved",
        title: "Messages Arrive as They're Sent",
        description:
          "New messages show up in the thread and in the conversation list the moment they're sent, with unread counts and ordering kept current, no refresh needed. Threads sort by their latest message, and you can delete a conversation or one of your own messages.",
      },
      {
        type: "improved",
        title: "Messaging on the Phone",
        description:
          "The launcher stays out of the way while a thread is open, so the composer and the last messages are never covered, keyboard up or down. Profile and shop links in the thread menu only appear when there is something to open.",
      },
      {
        type: "new",
        title: "Shop-Aware Barber Dashboard",
        description:
          "A barber with a chair now sees their booth rentals and the week's upcoming sessions at the top of the dashboard, above the wallet. The Booths page keeps the full picture: rentals, sessions, saved shops, agreements, and rent payments.",
      },
      {
        type: "new",
        title: "Top-Left Action in the App",
        description:
          "A pill in the top-left corner on every screen: Dashboard when you're elsewhere in the app, and on the dashboard the thing each mode reaches for most: Rewards for barbers, Messages for owners, Explore for clients.",
      },
      {
        type: "improved",
        title: "Browse Shops: Shuffle by Default, Most Popular by Visits",
        description:
          "Listings open in a shuffled order so no shop is always first. Most popular now ranks strictly by how often a listing is visited. Filters keep the map alive while you narrow results, and map pins are smaller and crisper at city zoom.",
      },
      {
        type: "improved",
        title: "Notifications Sheet",
        description:
          "The notifications panel on the phone has the app's rounded bottom edge and a grip to pull it taller or shorter. The launcher steps aside while it's open.",
      },
      {
        type: "improved",
        title: "Landing on Mobile",
        description:
          "Safari's bars now match the page instead of showing white or black bands, and the same treatment applies across every dark page. The hero fills the first screen, the barber tab's storefront photo is framed on the pole, the mission text lights as you scroll without pinning the page, and switching Barber and Owner returns you to the top. Product videos are a fraction of their previous size and load only as they come into view.",
      },
      {
        type: "improved",
        title: "Fields Don't Zoom on iPhone",
        description:
          "Tapping a search or login field no longer zooms the page in on iOS.",
      },
      {
        type: "improved",
        title: "Search Page Closes for Both Sides",
        description:
          "At the foot of search results, barbers get Create free account and shop owners get List your shop, side by side. Search is open to visitors before signing up, and full shops stay in results after the ones with an open chair.",
      },
      {
        type: "improved",
        title: "Pricing Copy",
        description:
          "The owner plan now says exactly what free covers: no card to list your shop or post openings, and Stripe only if you choose to collect rent through BOOTH.",
      },
      {
        type: "removed",
        title: "Client Tab on the Landing",
        description:
          "The landing switches between Barber and Owner only. Client mode is still there inside the app; it just doesn't need its own tab on the marketing page.",
      },
    ],
  },
  {
    date: "September 16, 2026",
    items: [
      {
        type: "new",
        title: "Search Shows How a Shop Is Hiring",
        description:
          "Every published shop has an open chair, so \"open booths\" told a barber nothing. Search now reads the job posts owners publish (title, pay, schedule, specialties) and puts them on the card, along with chairs open and rental cadence. Shops with a posted job rank first, and words like \"commission\" or \"fades\" find them.",
      },
      {
        type: "new",
        title: "Price Filter on Search",
        description:
          "One control, right of the results: Any price, Under $500, $500 to $1,000, or Over $1,000 a month. Chairs, plan, and hiring filters were tried as rows and removed. Where you want to work is what the search box is for.",
      },
      {
        type: "improved",
        title: "Search Reads Cleaner",
        description:
          "City labels are normalized, so \"chicago, Illinois\" and \"Chicago, IL\" are one place, and international shops read as \"London, UK\" instead of \"London, Greater London.\" The search pill and chips now follow the Figma spec (near-black glass fill, hairline stroke inside), the rotating headline and placeholder trade off on one slow beat with a soft drift instead of a fast bounce, the pinned search bar floats over results without a black slab behind it, and the signed-in header pill says Dashboard instead of Learn more.",
      },
      {
        type: "improved",
        title: "Pricing Page on the Glass Design System",
        description:
          "The owner plan sat in a flat gray box and the audience switch was painted light on a dark page. Both now use the same glass surfaces, tab control, and light/dark toggle as the landing and articles.",
      },
      {
        type: "new",
        title: "Barber Booths Page Rebuilt",
        description:
          "Your Booth Rentals is a grid of chair cards (the floor plan's booth tile, turned to face the barber) with the shop, city, and what the chair costs. Upcoming Sessions is a week strip plus a card grid: shop logo on every card, the shop's own slot hours, a live chip on today's session (Starts in 2h, Locked · Done @5PM, Wrapped up), and tap to reveal Reschedule or Cancel, which now works on a phone. Booth Payments matches the Documents list. Team Roster left the page.",
      },
      {
        type: "improved",
        title: "Sessions Page Shows Every Plan",
        description:
          "A barber renting at two shops has two plans, and the page only ever showed the first. A switch at the top now moves between shops; the plan card shows progress, the calendar marks your own days, and the session grid is the same one Booths uses.",
      },
      {
        type: "improved",
        title: "Dashboards Default to Light Mode",
        description:
          "The app followed the phone's system setting, so a phone in dark mode opened the barber and owner dashboards dark without anyone choosing it. Light is the default now; the Light / Dark toggle under Account still wins.",
      },
      {
        type: "improved",
        title: "Loading Counts Up",
        description:
          "Full page loads show a number climbing toward 99 instead of three pulsing dots, so a wait reads as progress rather than a spinner.",
      },
      {
        type: "new",
        title: "Article: Making the Barber Market Searchable",
        description:
          "A founder piece on why search is the next layer of BOOTH: barbers have always found opportunity through other barbers, and the network that already exists between shops, cities, and events should be more visible. Honest that supply is still being built. It ships with a new drawn thumbnail: a barber's route across a city.",
      },
      {
        type: "improved",
        title: "Every Article Rewritten in the BOOTH Voice",
        description:
          "All ten existing articles rewritten to a new editorial guide, in two voices: Eduardo on product and thesis, Paul on numbers, finance, and operations. Facts, figures, product steps, dates, and thumbnails are unchanged. The booth mark now sits on both bylines.",
      },
      {
        type: "new",
        title: "Editorial Voice Guide",
        description:
          "docs/editorial-voice.md sets how BOOTH writes: observe the industry before the technology, say plainly what exists today versus what's being built, end on the idea rather than a slogan. 700 to 900 words, four to six sections, one idea each.",
      },
    ],
  },
  {
    date: "September 6, 2026",
    items: [
      {
        type: "improved",
        title: "Search Is Open to Everyone",
        description:
          "/search is explicitly public in the app's route rules, so a barber without an account can search before signing up. Smaller wordmark on search, listings, and the sign in and sign up pages; the sign-in gate on a listing centers its mark.",
      },
    ],
  },
  {
    date: "September 5, 2026",
    items: [
      {
        type: "new",
        title: "Search for a Shop to Work At",
        description:
          "A new page, /search, for barbers looking for their next chair. Type a city, or a sentence like \"walk-in shop in Chicago, 5+ chairs, under $250 a week,\" and get shops with open booths from the live listings feed. No account needed to look. Search is linked from the nav, the footer, and the barber landing.",
      },
      {
        type: "improved",
        title: "Deposits & Payouts Redesigned",
        description:
          "Gray card containers replaced with flat lists on the page. Payout status is live: if the balance check fails it says why and offers Try again, instead of sitting on \"Checking schedule\" forever.",
      },
      {
        type: "fixed",
        title: "Pricing Tabs Stacked Both Plans",
        description:
          "Switching between Owner and Barber pricing could render both plans on top of each other. Fixed. The For Clients tab is gone; clients don't pay on BOOTH.",
      },
      {
        type: "fixed",
        title: "Occupancy Read Empty Before the Day Was Over",
        description:
          "The Day view measured hours up to the current minute, so a chair booked solid this afternoon read as empty at 9am. The window now runs to the end of the day.",
      },
      {
        type: "fixed",
        title: "Landing Page Scroll Jank",
        description:
          "The scroll-linked text and stats sections ran a layout read on every frame, even off screen. They now only run while visible and stop once settled. Noticeably smoother on phones.",
      },
      {
        type: "fixed",
        title: "Dark Mode Fixes",
        description:
          "The floor plan's opportunity bar was stuck white in dark mode, booth cards clipped their occupancy bar, and the two-factor sign-in page rendered white text on white. All fixed. Dark mode's background is true black now.",
      },
      {
        type: "improved",
        title: "Owner Dashboard Order and Listing Cards",
        description:
          "Shop Roster sits above Barber Schedules. Schedule avatars are smaller and borderless. On a shop's listing page, booth option cards were restyled with a real Contact Shop button and more contrast.",
      },
    ],
  },
  {
    date: "September 4, 2026",
    items: [
      {
        type: "fixed",
        title: "BOOTHmcp Confirmed Live in Production",
        description:
          "Resolved a dependency version mismatch in the build pipeline and verified BOOTHmcp end to end. It's live and answering real availability searches at www.boothapp.co/api/mcp.",
      },
      {
        type: "fixed",
        title: "MCP Tools No Longer Flagged \"Destructive\"",
        description:
          "Claude and ChatGPT were labeling our read-only availability checks as \"Public Write\" / \"Destructive\" because we never told them otherwise. All three tools now correctly declare themselves read-only, so the warning a barber sees actually matches what the tool does.",
      },
      {
        type: "new",
        title: "MCP Shows Total Booths, Not Just Open Ones",
        description:
          "Ask an AI assistant about a shop and you now get both numbers together — \"11-Booth Shop — 2 Available Booths\" — instead of a bare open count with no sense of shop size.",
      },
      {
        type: "new",
        title: "MCP Points Barbers Toward an Account",
        description:
          "Every MCP result now carries a signup link alongside the listing link, and the tool descriptions spell out that a free BOOTH account is what actually lets a barber request a booth — not just how to browse.",
      },
      {
        type: "improved",
        title: "MCP Guards Against Manipulated Shop Listings",
        description:
          "Shop owners control their own description and plan text. Added a filter that keeps a listing out of AI search results if its content looks like it's trying to instruct the assistant rather than describe the shop.",
      },
      {
        type: "improved",
        title: "Owner Landing: Post an Opening, Not Manage Barbers",
        description:
          "The owner hero button used to send you to a raw barber list. It now says \"Post an opening\" and prompts sign in/up first — hiring starts with your account, not a browse page.",
      },
      {
        type: "fixed",
        title: "Listings Page No Longer Shows the Wrong Nav Link",
        description:
          "A logged-out visitor on a shop listing page was stuck seeing \"Find Barbers\" — an owner action that made no sense there. It now shows Articles, and the language switcher (which had nothing to do on a single listing) is gone.",
      },
      {
        type: "improved",
        title: "Landing Page Visual Pass",
        description:
          "Card backgrounds moved from a flat gray wash to a black-tinted glass look with a proper highlight, the footer is now true black instead of blending into the page above it, and the hero's shop photo is more visible in both light and dark mode.",
      },
    ],
  },
  {
    date: "September 3, 2026",
    items: [
      {
        type: "new",
        title: "BOOTHmcp — Search BOOTH From Claude or ChatGPT",
        description:
          "A free, public MCP server that lets AI assistants search real booth availability, pricing, and shop details directly in a conversation — no account needed to look. Built around rental plans, not a date/session calendar, since finding a shop to work from isn't the same question as booking an appointment.",
      },
      {
        type: "fixed",
        title: "Social Share Preview Image",
        description:
          "Links to BOOTH on social media were showing a cropped white logo, invisible on a white background. Replaced with a proper black-on-white mark.",
      },
    ],
  },
  {
    date: "September 2, 2026",
    items: [
      {
        type: "new",
        title: "Live Traction Tracker on the Landing Page",
        description: "Real platform stats, scroll-revealed on the homepage instead of buried in a press page.",
      },
      {
        type: "improved",
        title: "Smoother Landing Scroll",
        description:
          "The traction section and the Mission intro's scroll-driven animation are smoother and less abrupt on the way in.",
      },
      {
        type: "new",
        title: "Urban Barbershop Performance, in Two Parts",
        description:
          "Split the urban performance article into two focused reads for shop owners, backed by sourced 2026 barber industry statistics.",
      },
    ],
  },
  {
    date: "September 1, 2026",
    items: [
      {
        type: "improved",
        title: "Pricing Page Refresh",
        description:
          "New page design with an animated FAQ accordion, defaults to the owner view (what a shop actually pays), and quotes the live 5% platform fee everywhere instead of copy that could drift out of sync with it.",
      },
      {
        type: "improved",
        title: "Owner Payments Section Rebuilt",
        description:
          "\"Stop chasing rent\" — autopay demo, real payment brand logos (Visa, Mastercard, Apple Pay, Google Pay), and a rewards card with an animated progress bar instead of a static number.",
      },
      {
        type: "improved",
        title: "BOOTH Journal and Help Guides, Unified",
        description:
          "Articles and how-to guides now live on one page with category filters and a date sort, instead of two separate, harder-to-browse sections.",
      },
      {
        type: "new",
        title: "Documenting Booth Rent for Funding",
        description:
          "New founder journal post on using documented rent and occupancy history to support a funding or expansion conversation.",
      },
      {
        type: "fixed",
        title: "Floor Plan Occupancy Bar",
        description: "Rented, currently-earning chairs were incorrectly showing as \"Closed this month.\" Fixed, and reordered the owner dashboard so the floor plan sits above schedules.",
      },
      {
        type: "improved",
        title: "Barbers Can Browse Shops Without Signing Up First",
        description: "Removed the signup gate on the public /barbers browse page — looking is free, an account is only needed to act.",
      },
      {
        type: "fixed",
        title: "Listings Map Performance",
        description: "Faster loading and crisper map tiles on the shop browse page.",
      },
    ],
  },
  {
    date: "August 31, 2026",
    items: [
      {
        type: "improved",
        title: "Owner Barber Profile, Redesigned",
        description:
          "The modal an owner opens on a roster barber now shows insights, schedule, and payment history together, with contact actions moved into the header instead of buried in the body.",
      },
      {
        type: "improved",
        title: "Help Guides Rewritten",
        description: "Rewritten in a clearer, more direct documentation style — easier to scan for the one step you actually need.",
      },
      {
        type: "improved",
        title: "More Pages Work Without Signing In",
        description:
          "/help, /payment, /shop, /about, and /contact no longer hit the signup wall — you shouldn't need an account just to read a help article or find contact info.",
      },
{
        type: "new",
        title: "Payment How-To Guides",
        description: "New step-by-step guides for both owners and barbers on how booth rent payments actually work on BOOTH.",
      },
      {
        type: "new",
        title: "Product Demo Videos on the Owner Landing Page",
        description: "Scroll-triggered demo videos, framed as real iPhone screens, showing the dashboard in actual use.",
      },
    ],
  },
  {
    date: "August 28, 2026",
    items: [
      {
        type: "new",
        title: "Help Center",
        description: "A dedicated help center launched, alongside a new payments section on the landing page.",
      },
    ],
  },
  {
    date: "August 27, 2026",
    items: [
      {
        type: "improved",
        title: "Clearer Two-Fee Breakdown",
        description: "The platform fee and the booking fee are two different charges — the pricing copy now says so plainly instead of blending them together.",
      },
    ],
  },
  {
    date: "August 26, 2026",
    items: [
      {
        type: "improved",
        title: "Platform Fee Updated to 5%",
        description: "BOOTH's platform fee moved from 3% to 5%, and every page that quotes it — pricing, checkout, payouts — now reads it live instead of from typed-in copy that could fall out of date.",
      },
      {
        type: "improved",
        title: "Booking Fee Now Has Its Own Dedicated Rate",
        description: "Deposits are billed against a dedicated booking fee rate, separate from rent, for cleaner accounting going forward.",
      },
      {
        type: "new",
        title: "Payouts, For Real",
        description: "The owner payouts screen now reports the actual payout schedule and can process a real payout, not just display one.",
      },
      {
        type: "improved",
        title: "Booth Plan Cards at Any Count",
        description: "Booth rental plan cards now use the full width of their column no matter how many plans a shop offers, instead of shrinking awkwardly past a certain count.",
      },
      {
        type: "improved",
        title: "Cleaner Map Tiles",
        description: "Replaced watermarked map tiles on the listings map with clean ones, and tightened spacing on mobile.",
      },
    ],
  },
  {
    date: "August 25, 2026",
    items: [
      {
        type: "improved",
        title: "Listings Filter Bar Rebuilt",
        description: "A cleaner filter bar on the shop browse page, and the owner's own listing preview now renders the actual live listing instead of a mock.",
      },
    ],
  },
  {
    date: "August 24, 2026",
    items: [
      {
        type: "improved",
        title: "Reserve, Not Book Now",
        description:
          "Booth plans now say Reserve. You're holding a chair, not buying a haircut, and nothing moves until you're through checkout — so the button says what it actually does.",
      },
      {
        type: "improved",
        title: "Every Plan Visible on Your Phone",
        description:
          "A shop listing four booth plans used to hide half of them behind a sideways swipe nobody knew to make. They now sit two-by-two on a phone and three across on a laptop, and each plan's full description is there — no more sentence cut off mid-thought with no way to read the rest.",
      },
      {
        type: "improved",
        title: "Long Shop Names Fit",
        description:
          "A shop with a long name no longer runs its title into the share and save buttons. The name steps down a size so it sits on one line, whatever you called your shop.",
      },
      {
        type: "improved",
        title: "A Cleaner Way In",
        description:
          "The sign-up prompt you get when reserving a chair is lighter and says less — one heading, one line, one button. The old version explained things you already knew from the page you were standing on.",
      },
      {
        type: "fixed",
        title: "Contact Owner Stays Readable",
        description:
          "Hovering the Contact Owner button made its label vanish — dark text on a background that went dark with it. The text holds its contrast now.",
      },
      {
        type: "fixed",
        title: "Menu Labels No Longer Touch the Edge",
        description:
          "In the main menu, longer labels like Browse Shops pressed right up against the panel edge. The panel is wider and the labels have room.",
      },
    ],
  },
  {
    date: "August 6, 2026",
    items: [
      {
        type: "improved",
        title: "Two Ways to Start",
        description:
          "New to BOOTH with no shop yet? Your dashboard now offers both paths up front — browse chairs for rent near you, or invite your shop owner to join. Before, the only option assumed you already had a shop.",
      },
      {
        type: "fixed",
        title: "Contact Owner, Tidied Up",
        description:
          "Shops without a logo no longer show an empty circle beside the Contact Owner button.",
      },
    ],
  },
  {
    date: "August 5, 2026",
    items: [
      {
        type: "new",
        title: "Booking Channels — Know Which Link Pays You",
        description:
          "Your booking link now comes tagged per platform: Instagram, TikTok, Facebook, X, Threads, YouTube, or text. One tap to copy. Then see three things about your own business — who's looking, who's actually booking, and what each source is worth in dollars. Attribution lasts 30 days, so a client who finds you Monday and books Thursday still counts.",
      },
      {
        type: "new",
        title: "Every Chair, On Its Own",
        description:
          "Shops with several chairs can now rent each one independently — different barbers, different days, different times on the same chair. A shop with eight chairs is no longer limited to filling one at a time.",
      },
      {
        type: "improved",
        title: "Earnings and Rent, One List",
        description:
          "Payment Activity now shows money in and money out together — client payments you received and booth rent you paid, newest first, each labelled for what it was.",
      },
      {
        type: "improved",
        title: "A Calmer Barber Dashboard",
        description:
          "Booking links are now two tap-to-copy rows instead of two large cards, and they sit near the top where you actually reach for them.",
      },
      {
        type: "fixed",
        title: "Straight to Your Dashboard",
        description:
          "Signed in and opening BOOTH now takes you to your dashboard instead of the marketing homepage.",
      },
    ],
  },
  {
    date: "August 2, 2026",
    items: [
      {
        type: "improved",
        title: "Dashboards That Fit the Screen",
        description:
          "Home dashboards sit in a narrower centred column on desktop, so your numbers stay readable instead of stretching the full width of a large monitor.",
      },
    ],
  },
  {
    date: "August 1, 2026",
    items: [
      {
        type: "new",
        title: "Read a Role Without Losing Your Place",
        description:
          "On the Job Board, tapping View role opens it right there in the list — read it, close it, keep scrolling.",
      },
      {
        type: "improved",
        title: "One Menu, Every Screen Size",
        description:
          "The navigation is now a single menu at every width. Pricing is reachable from every mode, signed in or out.",
      },
      {
        type: "improved",
        title: "Clients See Haircut Prices",
        description:
          "Browsing in Client mode now shows service prices, never booth rent. Two different audiences, two different numbers.",
      },
    ],
  },
  {
    date: "July 30, 2026",
    items: [
      {
        type: "new",
        title: "A New Front Door",
        description:
          "The homepage now speaks to whoever you are — barber, owner, or client — with real photography from shops on BOOTH instead of stock imagery.",
      },
    ],
  },
  {
    date: "July 28, 2026",
    items: [
      {
        type: "new",
        title: "Partner Rewards",
        description:
          "Rewards now includes offers from partner brands, starting with our Chicago pilot. More markets to follow.",
      },
    ],
  },
  {
    date: "July 27, 2026",
    items: [
      {
        type: "new",
        title: "Views, Bookings, and Revenue by Source",
        description:
          "Your channel breakdown splits into three honest views: who looked, who booked, and what each source earned. Attention and income are rarely the same story.",
      },
      {
        type: "fixed",
        title: "Staying Signed In",
        description:
          "Closing your browser no longer signs you out, and the dashboard no longer gets stuck loading.",
      },
      {
        type: "improved",
        title: "Clearer Pricing",
        description:
          "Our pricing page now states plainly that 0% means nothing is taken out of your rent.",
      },
    ],
  },
  {
    date: "July 21, 2026",
    items: [
      {
        type: "new",
        title: "Status Page",
        description:
          "A public status page so you can check whether it's BOOTH or your connection.",
      },
      {
        type: "improved",
        title: "Know What Payouts Still Need",
        description:
          "If your payout setup is incomplete, BOOTH now tells you exactly what's missing instead of leaving you guessing.",
      },
      {
        type: "improved",
        title: "Faster Dashboards",
        description:
          "Charts load separately from the rest of the page and the heaviest screens make far fewer trips to the server. Noticeably quicker on mobile data.",
      },
      {
        type: "fixed",
        title: "Withdrawals and Schedules",
        description:
          "Fixed barbers being unable to withdraw, and made the weekly schedule reachable again with your adjusted hours showing.",
      },
    ],
  },
  {
    date: "July 20, 2026",
    items: [
      {
        type: "improved",
        title: "Your Team, Its Own Section",
        description:
          "Team Roster moved out of the shop card into its own section, with room to breathe.",
      },
      {
        type: "improved",
        title: "Booking Pages in Dark Mode",
        description:
          "Booking pages now follow your theme properly — no more light-only colours on a dark screen.",
      },
    ],
  },
  {
    date: "July 19, 2026",
    items: [
      {
        type: "new",
        title: "Sign In With Google",
        description:
          "One tap to sign in or create an account. The login and signup screens were redesigned around it.",
      },
      {
        type: "improved",
        title: "A Proper Welcome",
        description:
          "Signing in now shows a brief BOOTH splash while your dashboard loads, instead of a blank screen.",
      },
      {
        type: "improved",
        title: "Open or Closed at a Glance",
        description:
          "Shop hours read more clearly on booking pages, with a cleaner closed sign and a quieter next-available indicator.",
      },
    ],
  },
  {
    date: "July 17, 2026",
    items: [
      {
        type: "improved",
        title: "Booking Links, Easier to Grab",
        description:
          "The booking link cards sit two-up on mobile with a cleaner URL and lighter type.",
      },
    ],
  },
  {
    date: "July 15, 2026",
    items: [
      {
        type: "improved",
        title: "Wallet Keeps Up",
        description:
          "Your wallet refreshes payout verification on its own instead of showing a stale banner, and payout buttons are more compact.",
      },
    ],
  },
  {
    date: "July 13, 2026",
    items: [
      {
        type: "improved",
        title: "Pricing and Refunds, in Plain Language",
        description:
          "Rewrote the refunds policy in plain English and tidied both pages on mobile.",
      },
    ],
  },
  {
    date: "July 11, 2026",
    items: [
      {
        type: "improved",
        title: "Roomier Home Screens",
        description:
          "Barber and owner dashboards narrow on desktop so the important numbers stay together.",
      },
    ],
  },
  {
    date: "July 10, 2026",
    items: [
      {
        type: "new",
        title: "Your Booking Links, One Tap Away",
        description:
          "A quick booking-links card on both the barber and owner dashboards — copy and share without hunting for it.",
      },
    ],
  },
  {
    date: "July 9, 2026",
    items: [
      {
        type: "improved",
        title: "A Faster App",
        description:
          "Hot paths now load in parallel across the app. Pages come up quicker, especially on mobile.",
      },
      {
        type: "fixed",
        title: "Logos and Profiles",
        description:
          "Shop logos with transparent backgrounds no longer render as black circles, and barbers can view their own hidden profile without hitting a 404.",
      },
    ],
  },
  {
    date: "July 8, 2026",
    items: [
      {
        type: "new",
        title: "Rewards in the Mobile Menu",
        description:
          "Rewards is now one tap from the barber mobile navigation.",
      },
      {
        type: "fixed",
        title: "Appointment Cards Fit",
        description:
          "Owner booking cards no longer overflow their row on mobile.",
      },
    ],
  },
  {
    date: "July 7, 2026",
    items: [
      {
        type: "improved",
        title: "Inviting a Barber, Start to Finish",
        description:
          "The invite flow was rebuilt end to end — the create-link button now shows a real completed state, the modal no longer cuts off, and the invite landing page is clearer about what you're joining.",
      },
      {
        type: "improved",
        title: "Smoother Shop Setup",
        description:
          "The create-shop wizard reads one question at a time without visual noise, and the navigation stays out of your way while you work through it.",
      },
    ],
  },
  {
    date: "July 6, 2026",
    items: [
      {
        type: "new",
        title: "BOOTH Bookings — $25/month",
        description:
          "The full client-booking suite for barbers: your booking page, appointments, payments, and reminders. Use code F&F2026 and your first year is free.",
      },
      {
        type: "new",
        title: "Refer a Barber",
        description:
          "Invite another barber to BOOTH and earn points when they join. Your referrals are tracked end to end.",
      },
    ],
  },
  {
    date: "July 5, 2026",
    items: [
      {
        type: "new",
        title: "The Gear Shop",
        description:
          "Spend your points on real equipment — clippers, trimmers, and more — shipped to you. Points come from paying your rent on time.",
      },
      {
        type: "new",
        title: "Know Your Week",
        description:
          "New analytics views show your cuts by weekday and the times of day you're busiest, so you can see your own rhythm.",
      },
      {
        type: "improved",
        title: "Earn Faster",
        description:
          "Auto-pay now earns double points, and paying on time builds a streak bonus.",
      },
    ],
  },
  {
    date: "July 4, 2026",
    items: [
      {
        type: "new",
        title: "Memberships You Can Join",
        description:
          "Barbers can offer recurring membership plans — and now clients can subscribe to one right from a barber's booking page. Manage it anytime from your account.",
      },
      {
        type: "new",
        title: "Barbers, Get Paid Through BOOTH",
        description:
          "Set up payouts and your earnings from client bookings land in your BOOTH wallet. See what's available, what's still settling, and withdraw straight to your bank — without leaving the app.",
      },
      {
        type: "new",
        title: "Booking Updates, In Your Inbox",
        description:
          "Confirmations, reschedules, cancellations, completed cuts, and no-shows now send an email to the right person — and new bookings come with a receipt.",
      },
      {
        type: "improved",
        title: "Manage Any Booking From the List",
        description:
          "Tap any appointment in your Upcoming or Past list to reschedule, call, message, or mark it complete — the same quick actions as the calendar. Every row now shows the client's photo and the price.",
      },
      {
        type: "new",
        title: "One-Tap Checkout",
        description:
          "Booking payments now support Apple Pay, Google Pay, and Link — pay in a single tap.",
      },
    ],
  },
  {
    date: "July 3, 2026",
    items: [
      {
        type: "new",
        title: "Client Photos on Your Schedule",
        description:
          "Your clients' profile photos now appear on your calendar and day view, so you recognize who's coming in at a glance.",
      },
      {
        type: "new",
        title: "The Gear Shop Is Open",
        description:
          "Redeem your rewards points for real barber gear — clippers, trimmers, shavers, and dryers from COCCO, Wahl, Andis, BaBylissPRO, TPOB, and Gamma+.",
      },
      {
        type: "improved",
        title: "A Warmer Welcome in Client Mode",
        description:
          "First-time clients now get a proper 'Welcome,' the account row shows your photo and which mode you're in, and Discover Barbers has a cleaner 'View Barber' link.",
      },
      {
        type: "improved",
        title: "Adding a Card Is Quicker",
        description:
          "The card fields now appear the moment you open Billing — no extra tap — and your saved card shows up instantly after you add it, no refresh needed.",
      },
    ],
  },
  {
    date: "July 2, 2026",
    items: [
      {
        type: "new",
        title: "Analytics for Your Chair",
        description:
          "The barber dashboard now shows your cut activity — cuts by weekday and when you work — so you can spot your busiest days at a glance.",
      },
      {
        type: "new",
        title: "Booking Emails",
        description:
          "Booking confirmations, reschedules, and cancellations now arrive by email for both you and your client, with booth-rental receipts for owners and barbers.",
      },
      {
        type: "improved",
        title: "Invite Barbers With a Link",
        description:
          "Owners can add a barber to their roster by sharing a simple link — no email or phone number required.",
      },
      {
        type: "improved",
        title: "Cleaner, Consistent Dashboards",
        description:
          "Barber and client dashboards now follow one calm, centered layout so everything feels familiar as you move across the app.",
      },
    ],
  },
  {
    date: "June 13, 2026",
    items: [
      {
        type: "new",
        title: "Change Your Email & Password in Settings",
        description:
          "Account → Security is now a clean home for both. Update your email address — we'll send a confirmation link to the new one to make sure it's really yours — and change your password with a live checklist that ticks off each requirement as you type. No more guessing whether your new password is strong enough.",
      },
      {
        type: "improved",
        title: "Booking Buttons Got a Glow-Up",
        description:
          "On a shop's page, the 'Book an appointment' link now shows the booking platform — Booksy, Squire, The Cut, or Fresha — as a crisp circular logo. Easier to recognize at a glance, and consistent for every platform.",
      },
      {
        type: "fixed",
        title: "Edit Shop: Booking Link & Deposit Are Back",
        description:
          "Your booking link and security deposit could be set when you first created a shop, but the Edit Shop page was missing both — so there was no way to change them later. They're back where they belong.",
      },
      {
        type: "fixed",
        title: "Create Shop Stays Open for Owners",
        description:
          "Owners opening 'Create Shop' could get bumped to the barber home and lose what they'd typed. The page now stays put and keeps your progress while you fill it out.",
      },
    ],
  },
  {
    date: "June 12, 2026",
    items: [
      {
        type: "improved",
        title: "Cleaner Shop Photo Viewer",
        description:
          "The full-screen photo gallery on a shop's page got a refresh — a soft blurred backdrop, smooth scroll-to-reveal, and no more white flash as you scroll through the shots.",
      },
      {
        type: "improved",
        title: "Traction Map, Front and Center",
        description:
          "The live locations map on the Traction page now opens right at the top, so you see where BOOTH is growing the moment the page loads.",
      },
      {
        type: "fixed",
        title: "Sign Up Button Fits Every Language",
        description:
          "The Sign Up button in the top nav was wrapping awkwardly in Spanish. Its text now sizes to fit cleanly no matter the language.",
      },
    ],
  },
  {
    date: "June 11, 2026",
    items: [
      {
        type: "new",
        title: "BOOTH Now Speaks Spanish",
        description:
          "Browse Shops, Find Barbers, shop pages, sign-up and login, and the account pop-ups are all available in Spanish. If your browser prefers Spanish, BOOTH offers to switch — and your choice follows you across the whole app.",
      },
      {
        type: "improved",
        title: "City Names, Done Right",
        description:
          "International cities read cleanly now — 'Ciudad de México' instead of ALL CAPS, with the familiar 'CDMX' short form — and different spellings of the same city merge into a single filter so you don't see duplicates.",
      },
      {
        type: "improved",
        title: "Traction, Now on a World Map",
        description:
          "The Traction page's plain list of locations became a dotted world map — a clearer, more striking look at where BOOTH is taking root around the globe.",
      },
      {
        type: "fixed",
        title: "Shop Social Icons on Mobile",
        description:
          "A shop's Instagram, TikTok, and YouTube links were hidden on phones. They now show on every screen size.",
      },
    ],
  },
  {
    date: "June 10, 2026",
    items: [
      {
        type: "improved",
        title: "Create Shop Remembers Your Draft",
        description:
          "Start listing a shop, step away to grab a detail, and come back — your text, amenities, and rental plans are right where you left them, so a tab switch never wipes your progress.",
      },
      {
        type: "fixed",
        title: "Pick Your Profile Photo From Your Gallery",
        description:
          "During onboarding you can now choose your profile photo straight from your phone's photo gallery, not just the camera.",
      },
    ],
  },
  {
    date: "June 7, 2026",
    items: [
      {
        type: "new",
        title: "Audience-Aware Top Bar",
        description:
          "The mobile landing bar's left action now follows the Barber/Owner toggle — barbers see Job Board, owners see Book a Demo. One slot, two doors: whoever you are, the first tap speaks to you.",
      },
    ],
  },
  {
    date: "June 6, 2026",
    items: [
      {
        type: "new",
        title: "Install BOOTH on Your Phone",
        description:
          "New banner on the mobile landing page teaches you something most people don't know: BOOTH installs straight from your browser to your home screen — no App Store needed. Tap 'See how' for a step-by-step guide that detects your device (iPhone Safari or Android Chrome) and shows only your steps. Full-screen app feel, opens in one tap, always up to date.",
      },
      {
        type: "improved",
        title: "Saved Shops, Front and Center",
        description:
          "On the barber home page, your Saved shops now sit above Upcoming Sessions — the shops you're considering deserve the top slot. The section header got bumped up to match 'My Booths' sizing, and a new 'Browse shops' link sits inline on the right so the path from saved → browsing is one tap.",
      },
      {
        type: "fixed",
        title: "Add New Shop Card Fits the Screen",
        description:
          "The owner home's empty-state card no longer runs past the bottom of the viewport — it now ends cleanly above the navigation button.",
      },
    ],
  },
  {
    date: "June 5, 2026",
    items: [
      {
        type: "improved",
        title: "One Nav, Every Screen",
        description:
          "The desktop nav modal now matches the mobile nav modal — same tile layout, same order, same feel. Switching between phone and desktop no longer means re-learning where things live. Avatar in the desktop pill got bigger and better-aligned too.",
      },
      {
        type: "improved",
        title: "Dashboard Pages Aligned to One Template",
        description:
          "Signed-in pages that used to float centered on desktop now left-align to the same column as the owner home — every dashboard page starts in the same place.",
      },
    ],
  },
  {
    date: "June 4, 2026",
    items: [
      {
        type: "new",
        title: "Mode Identity Header",
        description:
          "Mobile dashboards now lead with who you are: avatar, name, and current mode (Barber or Owner) sit above the greeting, so you always know which side of BOOTH you're on at a glance.",
      },
      {
        type: "new",
        title: "Map Opens on Your Location",
        description:
          "The /listings map now opens centered on where you actually are instead of a default pin. Finding shops near you starts near you.",
      },
      {
        type: "improved",
        title: "One Radius to Rule Them All",
        description:
          "Card corners standardized across the entire app — shop cards, nav modals, dashboard cards all share the same generous rounding, so every surface reads as one system.",
      },
      {
        type: "fixed",
        title: "Signed-In Users Land on Their Dashboard",
        description:
          "Logged-in users who hit the landing page (or tap the BOOTH wordmark) now route straight to their dashboard instead of the marketing page.",
      },
    ],
  },
  {
    date: "June 2, 2026",
    items: [
      {
        type: "new",
        title: "Clean White Top Bar on Mobile",
        description:
          "The mobile landing page got a proper top bar — BOOTH wordmark centered, Sign Up pinned right, white background that blends seamlessly with the browser. Slides away when you scroll down, returns when you scroll up.",
      },
      {
        type: "improved",
        title: "Landing Page Reordered",
        description:
          "Mission and ecosystem sections lifted above Opportunities so the story leads with why BOOTH exists before showing what's available.",
      },
    ],
  },
  {
    date: "May 26, 2026",
    items: [
      {
        type: "improved",
        title: "Hiring + Listings Polish Pass",
        description:
          "Job board and listings cards cleaned up across mobile and desktop. Filter row on /listings split into two independent scrollers so position filters and view options don't fight for the same swipe. Role pages: the ACTIVE pill's pulse dot now leads the label, and owners can see a view counter on their own postings.",
      },
      {
        type: "improved",
        title: "Nav Modals Decluttered",
        description:
          "Dropped the X close button from the nav modals (mobile and desktop) — tapping anywhere outside already closes them, and the tiles breathe better without chrome they don't need.",
      },
      {
        type: "fixed",
        title: "Listing Page Load Flash",
        description:
          "Killed the brief flash of the wrong header chrome when opening a listing detail page while signed in.",
      },
    ],
  },
  {
    date: "May 23, 2026",
    items: [
      {
        type: "new",
        title: "Job Board — Grid and List Views",
        description:
          "The public job board now has two real view modes. Grid (the new default) shows gallery-style cards in a 2×2 desktop layout; List is a compact, scannable row view — pulsing dot, avatar, role title with shop and city, chips, and time posted. Pick whichever way you like to hunt.",
      },
      {
        type: "improved",
        title: "Wider Job Board on Desktop",
        description:
          "The board below the map now stretches to match the map's width — more room for roles, less dead space.",
      },
    ],
  },
  {
    date: "May 21, 2026",
    items: [
      {
        type: "improved",
        title: "Welcome Emails, BOOTH Voice",
        description:
          "The welcome and follow-up emails for new barbers and owners got a full rewrite — founder vision up front, clear next step (build your portfolio, join the community), and the real BOOTH wordmark in the header. Less template, more us.",
      },
      {
        type: "improved",
        title: "Mobile Chrome Adapts to Dark Sections",
        description:
          "On the landing page, the mobile status-bar area now inverts over dark sections so the clock and battery stay readable. Blog cards also picked up the rounder card radius.",
      },
    ],
  },
  {
    date: "May 19, 2026",
    items: [
      {
        type: "new",
        title: "Onboarding Email Series",
        description:
          "New three-step welcome series for everyone who joins BOOTH — a warm welcome when you sign up, a feature tour a couple days in, and a nudge toward the community. Written in BOOTH voice, not autoresponder voice.",
      },
      {
        type: "improved",
        title: "Community Section Repositioned",
        description:
          "The Community strip on the landing page moved to second-to-last so the page flows from product story to community proof to the final call-to-action.",
      },
    ],
  },
  {
    date: "May 14, 2026",
    items: [
      {
        type: "new",
        title: "Job Board Cards — Full Spec Display",
        description:
          "Public job board (/hiring) cards now render every dimension of a role, not just position + comp. Specialties (capped at 3 with +N), experience required (skipped when 'any'), and the first preferred-clientele tag all show up alongside the existing position type and compensation pills. Labels resolve via a shared @/lib/hiring-labels module so the card text and the role detail page can never drift.",
      },
      {
        type: "improved",
        title: "Job Board Card Layout — Header Outside the Card",
        description:
          "Each role card now splits in two: the shop header (avatar + shop name + city) sits outside the white container, and the white container holds only the role itself — title, chips, time-ago pinned top-right, and a '+ View role' text link. Reads like a real list item: shop framing above, role detail below. Card itself is minimal/transparent (ring-[0.5px] hairline, very light hover).",
      },
      {
        type: "new",
        title: "Live Posting Pulse",
        description:
          "Every role on the job board now leads with a small pulsing dark-green dot (#0E542B) before the title — same ping pattern used on the Setup Stripe nudge. Reads as a live signal at a glance.",
      },
      {
        type: "improved",
        title: "City Filter — Dropdown, Not Chips",
        description:
          "City filter on /hiring is no longer a row of colored chips. It's now a thin MapPin + select dropdown defaulting to 'All cities'. Distinct treatment from the tone-coded position/comp/specialty/experience chips so location reads as a separate kind of filter.",
      },
      {
        type: "new",
        title: "Social Media URL on Job Postings",
        description:
          "Owners can paste an optional Instagram/flyer URL when posting a role (Edit Posting → Social media URL). Renders on the role detail as a small 'View original announcement ↗' link. Useful when the shop already announced the opening on IG and wants to point barbers at the original.",
      },
      {
        type: "improved",
        title: "Listing Detail — Personalized Contact Button",
        description:
          "The Contact button in the Questions card now shows the shop's logo as a small avatar inside the pill and reads 'Contact Owner' (or 'Contact Management' if the shop opts for the Management label in Edit Shop). Hover: subtle scale + shadow lift + ArrowRight icon nudge. Mobile and desktop variants both updated.",
      },
      {
        type: "new",
        title: "Owner / Management Contact Label",
        description:
          "New optional field on shops — Owner (default, more personal) or Management (better for franchise/multi-shop). Drives the Contact button copy on the public listing. Settable via Create Shop and Edit Shop with a small segmented toggle.",
      },
      {
        type: "improved",
        title: "Listing Detail — Layout Restructure",
        description:
          "View Shop Listing's lower section reorganized: Booth Options + Open Positions sit side-by-side on lg; Amenities span full width with a multi-col internal grid (so 16 amenities don't tower vertically); Location heading + address sit above a full-width map. Map gets a calm iPhone-radius (rounded-[2.5rem]). Questions card width narrowed (md:w-[320px]) so the description has more breathing room.",
      },
      {
        type: "improved",
        title: "Booth Options & Booking Buttons Hover",
        description:
          "Book Now, Contact Shop (booth-plan), and Book online buttons get the same hover treatment as Contact Owner — subtle scale-[1.02] + shadow lift + an ArrowRight that sits visible by default and nudges right on hover. Disabled Book Now opts out of the scale/shadow.",
      },
      {
        type: "improved",
        title: "Listings Map Follows the City Filter",
        description:
          "/listings and /hiring city pills now drive the map. Click a city → the map flies to fit the bounds of all matching markers (zoom 13 max, 0.9s ease). Single-match cities use flyTo at zoom 13. The city pill list itself is now derived from the actual loaded listings (was a hardcoded US-only set), so international shops show automatically.",
      },
      {
        type: "improved",
        title: "Switch Mode Cards Refresh",
        description:
          "Account Settings → Switch Mode redesigned to feel minimal/transparent. Heavy border-2 dropped for a hairline ring-[0.5px] foreground/10. Active state is a slightly darker ring + faint bg tint instead of a chunky black border. Icons render as bare emerald-700 strokes (no colored circle behind them). Reads as a calm binary choice.",
      },
      {
        type: "improved",
        title: "Floating Nav Popouts — Tighter Density",
        description:
          "Both mobile and desktop floating-nav popouts tightened to match the public popout density. Tile gap-y-5 → gap-y-3, top padding pulled in, account row shrunk (smaller avatar, tighter text scale, slimmer chevron). Reads as a focused launcher, not a chunky tray.",
      },
      {
        type: "new",
        title: "Empty My Booths CTA",
        description:
          "Barber dashboard's empty My Booths card now uses a Plus icon (was an arrow) — the action is creation, not navigation. Card width capped at lg:max-w-2xl so the empty-state CTA reads focused instead of stretched. Subtle scale on the icon on hover.",
      },
      {
        type: "improved",
        title: "Hiring Filter Chips — Color-Coded",
        description:
          "Filter chips on the public job board restored to their tone-coded colors: amber for position type, sky for compensation, rose for posted-within, violet for specialty, zinc for experience. Colors carry meaning per category. City filter is the lone exception — it's a dropdown.",
      },
      {
        type: "improved",
        title: "Listings Breathing Room",
        description:
          "Job board listings spaced out — list view space-y-2 → space-y-8 lg:space-y-10; grid view gap bumped to gap-6/8/10. Reads as discrete listings instead of a stacked block.",
      },
      {
        type: "improved",
        title: "'Show All Photos' Button Refresh",
        description:
          "Listing gallery's 'Show all photos' button is now transparent with a thin white outline (ring-[0.5px] ring-white/60), white font-light text, subtle backdrop-blur. Was a solid white pill.",
      },
      {
        type: "improved",
        title: "Contact Modal Refresh",
        description:
          "Contact Shop modal gets the BOOTH wordmark at the top, rounded-3xl card + rounded-2xl textarea, and natural-width Cancel + Send Message pill buttons (no longer full-width).",
      },
      {
        type: "improved",
        title: "Owner Preview Banner Refresh",
        description:
          "When an owner previews their shop listing, the top banner is now a contained rounded-2xl pill (was a full-bleed square bar) with a hairline ring border, plain 'Preview — how barbers see your listing' text, and rounded-full button pills.",
      },
      {
        type: "improved",
        title: "Mobile Landing — Bigger Black Sections",
        description:
          "Hiring + Booking Platforms sections on the landing page take min-h-[137svh] on mobile (was 110svh), 25% taller. Reads as full-screen black panels with content vertically centered. Desktop sizing untouched.",
      },
      {
        type: "improved",
        title: "Owner Hero Two-Line Headline",
        description:
          "Owner-mode landing hero 'Empty chairs / don't pay rent.' breaks into two lines on md+, matching the barber-mode 'More shops / More opportunity' pattern. Subhead bumped to text-base sm:text-lg lg:text-xl.",
      },
      {
        type: "new",
        title: "/hire Ad Redirect",
        description:
          "boothapp.co/hire now server-redirects to /auth/signup?role=barber. Used in 'We're Hiring' ad campaigns — barbers click through and land in the right onboarding lane with their role pre-selected.",
      },
      {
        type: "improved",
        title: "Public Footer Cleanup",
        description:
          "Numbered prefixes (01/02/03/04) dropped from the footer column headers (Get Started, Discover, Company, Legal). 'Built for the barber world · Worldwide' tagline removed from the bottom row — copyright is the only line now.",
      },
      {
        type: "improved",
        title: "Edit Posting Header",
        description:
          "Edit Posting page's sticky header dropped its bottom underline; '60d left' label now aligns with the form column edge below instead of floating against the viewport edge.",
      },
      {
        type: "new",
        title: "Sanctuary ATL & Starlite CDMX",
        description:
          "Two more shops added to the directory — Sanctuary ATL Barber Co. (Atlanta, Old Fourth Ward, owner-operated, bilingual EN/ES) and Starlite Barber Shop (Roma Norte, CDMX, vintage Americana with checkered floors). Brings total active seeded shops to 14.",
      },
    ],
  },
  {
    date: "May 13, 2026",
    items: [
      {
        type: "improved",
        title: "Listing Detail Dashboard Layout",
        description:
          "View Shop Listing went through a major restructure pass on desktop. Booth Options + Open Positions side-by-side; Amenities full width; Location + Map full width below. Heading anchors to the same x-position as the gallery image. Shop name + content stack aligned to lg:pl-7.",
      },
      {
        type: "improved",
        title: "Share / Save Adopt Density-Toggle UI",
        description:
          "Share + Save buttons on the listing detail header now use the same segmented-pill pattern as the /listings density toggle — bg-muted outer, p-0.5 rounded-lg, p-1.5 rounded-md buttons with bg-background + shadow-sm active state for the bookmarked state.",
      },
      {
        type: "new",
        title: "Shop Social URLs",
        description:
          "Shops can now save Instagram, TikTok, and YouTube URLs (Edit Shop). When populated, small black-stroke icons render in a row before the Share/Save segmented pill on the listing detail header.",
      },
      {
        type: "improved",
        title: "/hiring Controls Tightened",
        description:
          "Job Board controls collapsed from 3 stacked rows to 1: 'Listings' h2 inline with the open-count, Filters trigger and view-mode toggle clustered on the right. Filter chip panel aligned to lg:pl-7 so chips line up with the heading and listing cards.",
      },
      {
        type: "new",
        title: "Hiring Split Layout Preview",
        description:
          "Internal /preview/hiring route shipped with a 60/40 split layout (map left, listings right). Sticky preview banner, noindex, doesn't touch the live /hiring. Used to evaluate whether the split outperforms the current sticky-map-on-top layout.",
      },
      {
        type: "improved",
        title: "Barber Dashboard Card Radii",
        description:
          "My Booths and Saved cards on the barber dashboard unified to rounded-[2.5rem] — matches the Browse Shops surface. Was a mix of rounded-2xl / rounded-3xl across the page.",
      },
      {
        type: "improved",
        title: "Desktop Floating Nav Pill",
        description:
          "Pill nudged 10px right on lg (left-2 → 18px); name/email card and modal anchor recomputed in sync.",
      },
    ],
  },
  {
    date: "May 12, 2026",
    items: [
      {
        type: "improved",
        title: "Browse Shops Map — Smarter Markers",
        description:
          "/listings map now adapts to zoom level — beacon dots when you're zoomed out (so the country reads at a glance), full glass chips with shop name + price when you're zoomed in. The map also stopped rebuilding on every scroll tick (huge perf win on mobile), is clamped to one Earth (no more horizontal infinite-scroll into the Pacific), and has a floor on its height so it can never collapse into a thin stripe between the navbar and the listing cards.",
      },
      {
        type: "improved",
        title: "Browse Shops — Dynamic City Filter",
        description:
          "City filter on /listings is now generated from the actual shop directory instead of a hand-curated list. Add a shop in a new city and the filter picks it up automatically. Also fixed a desktop clipping bug where the filter pill was getting cut off at certain viewport widths.",
      },
      {
        type: "improved",
        title: "Listing Cards — Bigger Text, Arrow Icon, Recency Sort",
        description:
          "Browse Shops cards bumped their type scale so prices and shop names read at desktop arm's length. Listings without a published price now show a small right-arrow icon (clearer 'tap to see' affordance than blank space). 'Most popular' sort is now recency-weighted — newer listings with strong views surface above older listings with the same view count.",
      },
      {
        type: "improved",
        title: "Hero Spec Label — Single Line, Sentence Case",
        description:
          "Architectural spec label on the rotating landing hero is now a single line in sentence case (was two lines, ALL CAPS). Reads cleaner against the photo and stops wrapping unpredictably across breakpoints.",
      },
      {
        type: "improved",
        title: "Footer Refresh — Spec Style",
        description:
          "Footer widened, sections numbered (01 / 02 / 03 …), headers set in a monospace face so the footer reads like a spec sheet — matches the architectural spec language we use on the landing hero.",
      },
      {
        type: "improved",
        title: "Less Green, More Breathing Room",
        description:
          "Account, Owner, and Hiring surfaces dialed back their use of brand green and added more vertical breathing room between sections. The product reads less 'demo' and more 'tool' as a result.",
      },
    ],
  },
  {
    date: "May 11, 2026",
    items: [
      {
        type: "new",
        title: "BOOTH is Free Forever",
        description:
          "Dropped the $75/mo Pro tier. BOOTH is free forever for barbers — no subscription, no paywall on the core directory + booking experience. Pricing page rewritten around the 3% platform fee (and what it actually pays for) instead of a tier comparison.",
      },
      {
        type: "new",
        title: "Refund Policy",
        description:
          "Added a concrete refund policy: BOOTH does not issue refunds on platform fees; refund decisions on the underlying booking are governed by the shop's own policies. Linked from the footer alongside Terms and Privacy.",
      },
      {
        type: "new",
        title: "Rotating Shop Hero on Landing",
        description:
          "The landing hero now cycles through a curated reel of real shops behind an architectural spec label. The label pins to the viewport (it doesn't scroll with the photo), the rotation is slow enough to actually look at each shop, and a pro-color filter unifies the photography across very different lighting conditions. Less motion, less darkness, more 'directory of great shops.'",
      },
      {
        type: "new",
        title: "Theme-Aware Floating Public Navbar",
        description:
          "Public navbar lifts off the page edge and floats over the content. It auto-hides on scroll-down and reveals on scroll-up. Sections marked dark automatically invert the BOOTH wordmark so it stays legible.",
      },
      {
        type: "new",
        title: "Talent Feed Redesign + Signup Gate",
        description:
          "/barbers swapped its directory grid for a single-column talent feed (think IG profile grid, not a search-result page). Filter panel now organizes by section with clearer hierarchy. Logged-out viewers see the first 6 cards then hit a soft signup gate.",
      },
      {
        type: "improved",
        title: "Edit Shop UX Overhaul",
        description:
          "Edit Shop now has dirty-state tracking — the Save button only lights up when there's something to save, and an unsaved-changes warning fires if you try to navigate away. Image uploads auto-commit. Save floats so it stays in reach as you scroll a long form. Visual pass too — reverted some over-designed white surfaces, adopted rounded-2xl across the form.",
      },
      {
        type: "improved",
        title: "Global Address Autocomplete on Shops",
        description:
          "Shop creation and Edit Shop both now use a global address autocomplete that actually shows suggestions. Selecting an address writes precise coordinates back to the record so the map marker lands on the actual storefront.",
      },
    ],
  },
  {
    date: "May 10, 2026",
    items: [
      {
        type: "new",
        title: "Listings Sort Mode",
        description:
          "Browse Shops gets a black popover sort menu — Most popular (default, ranked by tracked listing views), Price: Low to High, and Price: High to Low. Trigger is a small pill that sits right-aligned above the density toggle on every breakpoint; URL-synced via ?sort= so a sort can be shared.",
      },
      {
        type: "new",
        title: "Booking Checkout Fee Breakdown",
        description:
          "Booking flow now itemizes Subtotal + Platform fee (3%) + Total before payment. A clickable Info icon on the platform-fee row explains what the 3% covers (Stripe processing + platform operations) so owners and barbers understand the line item before they pay.",
      },
      {
        type: "improved",
        title: "Footer Refactor",
        description:
          "New shared <PublicFooter /> across landing/blog/privacy/terms/press/articles — white background, black headers, gray subtext, role-aware 'For You' column (public / barber / owner). Social row uses text labels (Instagram · X · TikTok) for consistent sizing, and mobile bottom padding lifts the © line clear of the floating chair button. Net ~300 lines of inline footer code deleted.",
      },
      {
        type: "improved",
        title: "Hero Refresh",
        description:
          "Solid white CTA pill with an arrow icon, refined 'Open Beta · Live' badge, and a radial vignette under the headline for cleaner readability over the hero photo. The barber-mode H1 also flipped from a question to a direct benefit — 'Make more as a barber, in more shops.'",
      },
      {
        type: "improved",
        title: "Features Grid Refresh",
        description:
          "Owner mode now highlights Hiring, Talent Discovery, and Recurring Rentals (replaced Powerful Insights / Multi-Shop / Connected Calendars). Barber mode now highlights Public Profile, Community Feed, and Opportunities. Grid compacted to max-w-4xl with text-xs titles and text-[11px] descriptions so the section reads at a glance.",
      },
      {
        type: "improved",
        title: "Public Navbar Modal Parity",
        description:
          "Mobile floating-nav and desktop public-navbar modals now mirror each other — Browse Shops, Community, Job Board, Pricing, Blog, and a Sign Up CTA tile. Changelog moved out of the navbar and into the footer where release notes belong.",
      },
      {
        type: "improved",
        title: "Notifications UX",
        description:
          "Both panels (mobile + desktop) refreshed — softer bell-active scale, ring-2 unread badge, mobile backdrop now dims, slide transition tuned to 280ms, and the panel tabs adopted the project's standard segmented-pill style. Mark-all-read uses the same small-chip styling as the rest of the dashboard.",
      },
      {
        type: "improved",
        title: "Community Masonry View",
        description:
          "/community's densest tab is now a true 5-column masonry — CSS columns + cycled aspect ratios + a subtle hover lift on each card. Looks like a portfolio wall instead of a uniform grid.",
      },
      {
        type: "improved",
        title: "Booking Flow Polish",
        description:
          "Booking pages now anchor to the dashboard's chrome offsets (md:pl-28 / lg:pl-32) so the heading sits below the navbar instead of overlapping it; content is centered on every breakpoint. The completed-step badge and success icon use the project's dark-green token (#0E542B) to match the rest of the booking palette.",
      },
      {
        type: "improved",
        title: "Floating Nav Pill Geometry",
        description:
          "Mobile pill: bigger avatar (w-[68px]) + chair (w-[60px]) with tighter px-1 / py-2 padding so the two circles fill the pill. Desktop pill: thinner (px-1.5), longer (py-4 + gap-3.5), and shifted left so it docks closer to the viewport edge. Modal anchors recomputed to match the new geometry.",
      },
      {
        type: "improved",
        title: "Terms of Service §6 — Shop Photo Rule",
        description:
          "Shop Owner Responsibilities now explicitly require listing photos to be of the actual barbershop — interior, exterior, signage, chairs, equipment, team. Haircut close-ups, before/after portfolio shots, and customer face photos are barber-profile content, not shop content. Off-topic uploads can result in listing removal or account termination.",
      },
      {
        type: "improved",
        title: "Public PageFrame Chrome",
        description:
          "Browse Shops, Community, and Job Board no longer hug the viewport edge on desktop — the public chrome bumped from lg:px-[1svw] to lg:px-8 so content sits in a consistent margin. /hiring and View Shop Listing also picked up lg:pl-7 on their headings so they anchor to the same x-position as Browse Shops.",
      },
      {
        type: "improved",
        title: "Dashboard Compaction Protocol",
        description:
          "Shop dashboard balance grid, Analytics Overview, Barber Schedules, Payment Activity, and the barber dashboard sections (Upcoming Sessions / Saved / Pending Requests / Payment Activity / Rewards) now share the same compaction — max-w-2xl, text-sm headings, text-[11px] hyperlinks. Shop dashboard header logo and name shrunk in lockstep. Action-row buttons drop secondary fill and use h-7 / text-xs across all breakpoints.",
      },
      {
        type: "fixed",
        title: "Job Board Grid View on Mobile",
        description:
          "The list/grid toggle on /hiring previously had a 2-col grid that only kicked in on lg+, so mobile / sm / md were identical regardless of which tab you picked. Grid now renders 2 columns on every breakpoint.",
      },
      {
        type: "fixed",
        title: "PublicAuthCta Width Lock",
        description:
          "Sign In / Sign Up button no longer jumps width when its label swaps. Both labels share a single CSS grid cell so the container width is fixed to the longer of the two — same trick as the role toggle on the auth pages.",
      },
      {
        type: "fixed",
        title: "Listing Card Gallery Radius",
        description:
          "View Shop Listing gallery containers bumped from rounded-xl to rounded-3xl to match the rest of the project's radius protocol.",
      },
      {
        type: "fixed",
        title: "Downvote Removed from Network Feeds",
        description:
          "Barber and owner network feeds are upvote-only now — the downvote button is gone from the UI. Existing downvote rows are preserved as harmless dead data so nothing breaks if we change our minds.",
      },
      {
        type: "new",
        title: "Landing Mode Toggle (Barber / Owner)",
        description:
          "Floating segmented pill at the top of the home page lets a visitor flip the marketing copy between barber and owner perspectives. Hero, Hiring section, and CTAs all rewrite themselves based on the choice — no more pretending to address both audiences in the same sentence. Default is Barber; persisted to localStorage so a return visitor lands in their last-chosen mode.",
      },
      {
        type: "new",
        title: "Barber-Mode Hero Image",
        description:
          "Owners still see the modern barbershop interior; barbers now see an open shop with empty styling chairs (Waroom Studio, Tainan). Same gradient overlay and animated subtle-zoom on both — only the photograph differs.",
      },
      {
        type: "improved",
        title: "Hiring CTAs Deep-Link to Signup",
        description:
          "On the landing page, logged-out 'Post an opening' (owner mode) sends users straight to /auth/signup?role=owner; 'Browse open positions' (barber mode) sends them to /auth/signup?role=barber. The role radio is pre-selected so they're one tap closer to the right onboarding lane.",
      },
      {
        type: "removed",
        title: "Local Owners/Barbers Tab in Features",
        description:
          "Killed the duplicate Owners/Barbers toggle inside the Features section. The section now reads from the global landing toggle — single source of truth, one less micro-decision on the page.",
      },
      {
        type: "improved",
        title: "Hiring → Single Source of Truth",
        description:
          "Consolidated the two owner hiring surfaces. The per-shop hiring listing page (/dashboard/owner/shop/[id]/hiring) now redirects to /hiring, which shows all listings across all your shops grouped by shop name. Same badge, same UI, same actions everywhere — no more two-pages-with-similar-content confusion. Net: ~190 lines of duplicate UI deleted.",
      },
      {
        type: "improved",
        title: "Shop Selector on New Posting",
        description:
          "Owners with multiple shops can now choose which shop a posting belongs to from a dropdown at the top of the new-posting form. Single-shop owners still see no picker — it only appears when there's more than one shop to disambiguate.",
      },
      {
        type: "improved",
        title: "Shop Dashboard 'Post a chair' Button",
        description:
          "The 'Hiring' button on each shop dashboard is now '+ Post a chair' — a shop-scoped action that opens the new posting form pre-filled for that shop. Skips the visit-global-then-pick-shop detour.",
      },
      {
        type: "improved",
        title: "Browse Shops — H1 + Section Label Split",
        description:
          "/listings now reads 'Browse Shops' as the page heading above the map, then a smaller 'Listings' label right above the cards (job-board pattern). H1 anchors to the map's '28 shops' badge edge on desktop (lg:pl-7) instead of floating to the left of it.",
      },
      {
        type: "improved",
        title: "Listings Density Toggle",
        description:
          "One toggle on /listings now controls grid density across all breakpoints — sparse (1 col mobile, 3 cols desktop) or dense (2 cols mobile, 5 cols desktop). Replaces the old mobile-only view switch and uses the same crispy segmented-pill UI as the /community view tabs.",
      },
      {
        type: "improved",
        title: "Listing Card Shape",
        description:
          "Browse Shops cards now mirror the Saved-shops shape on the barber dashboard — rounded-3xl corners, 4:3 aspect ratio. Cards feel like one design language across both surfaces.",
      },
      {
        type: "improved",
        title: "Community Feed View Modes",
        description:
          "Densest tab on /community now goes to 5 columns on desktop (was 3) so the three view modes — 5 cols / 2 cols / single — are genuinely distinct. Avatars, names, and locations on each card shrunk by roughly a third so the image (the actual content) breathes.",
      },
      {
        type: "improved",
        title: "Public Navbar Refresh",
        description:
          "Smaller, tracking-tight nav links (Browse Shops, Community) plus a Sign Up button that matches the project's standard pill — no more gradient, no more hover-scale. Modal flyout sits below the navbar (top-[4.5rem]) with a smoother slide-in. Rewards moved out of the modal into the footer; Sign Up promoted into the bottom-right tile slot as a CTA.",
      },
      {
        type: "improved",
        title: "Dashboard Cleanup Sweep",
        description:
          "Several owner-side surfaces tightened to feel less heavy on desktop. Stripe Connected banner caps at max-w-md with a smaller inline 'View Stripe Dashboard' button. Withdrawal / Request actions become compact left-aligned pills on md+ (mobile keeps full-width thumb-friendly slabs). Shop Roster avatars (w-14 → w-10) and text shrunk; >6 barbers now switches to a 3/6-col grid instead of overflowing the row. Shop Roster moved above Analytics Overview — action before metrics.",
      },
      {
        type: "improved",
        title: "Hiring Badge Unified",
        description:
          "ACTIVE badge on hiring postings now uses one style across both /hiring and the per-shop view — small dark-green pill with a pulsing dot, matched on width (px-1, gap-0.5, leading-tight). Was rendering two slightly different sizes/colors before.",
      },
      {
        type: "improved",
        title: "Switch Mode Cards Unified",
        description:
          "Account Settings → Switch Mode now renders both Barber and Owner cards with the same green outline + white background + green-filled badge when selected. Previously the Barber selection used the primary (black) palette and Owner used a faint green tint — now they read as a true binary choice. Header arrow + 'Account Settings' tightened from gap-3 to gap-1.",
      },
      {
        type: "improved",
        title: "Form Pages Aligned to Dashboard Chrome",
        description:
          "Create New Shop and Edit Shop now use the standard md:pl-28 / lg:pl-32 chrome offsets so the form anchors right of the floating pill instead of drifting toward viewport center. Headings sit where every other dashboard page's heading sits. Edit Shop content also dropped from max-w-4xl → max-w-3xl with tighter header padding.",
      },
      {
        type: "fixed",
        title: "Public Viewers on Shop Listing",
        description:
          "/listings/[id] no longer applies the dashboard's left-pill offset to logged-out viewers — content now sits in the public chrome (centered, lg:px-[1svw]). Top padding also tightened so the heading reads in line with Browse Shops.",
      },
      {
        type: "fixed",
        title: "Mobile Heading Positions Smoothed",
        description:
          "The dramatic shift in heading position when crossing the sm → md breakpoint (where the public navbar appears) is gone. /listings, /hiring, and their owner-view variants land at the same vertical position relative to the visible top chrome on every breakpoint. Account Settings and Manage Hiring also got their mobile top padding bumped so the heading clears the floating BOOTH wordmark.",
      },
      {
        type: "fixed",
        title: "Security Deposit Simplified",
        description:
          "Removed the blue info icon and white card framing from the Security Deposit row on shop listings. The deposit dollar amount now uses the same dark green (#0E542B) as the booth-plan price below it — the two prices visually rhyme instead of competing.",
      },
    ],
  },
  {
    date: "May 9, 2026",
    items: [
      {
        type: "new",
        title: "PageFrame Chrome Modes",
        description:
          "Introduced a single source of truth for page padding — every screen now declares its chrome via PageFrame: 'public' (lg:px-8, used on landing-style pages) or 'app' (md:pl-28 / lg:pl-32, anchored to the floating nav pill). Replaces ~10 different ad-hoc padding strategies across /hiring, /listings, /community, and the dashboard.",
      },
      {
        type: "improved",
        title: "Public Navbar Modal",
        description:
          "Public navbar's flyout now mirrors the mobile FloatingNav — solid black panel with the same tiles, a Changelog row added, and the sign-out icon + standalone Log In link removed (Sign Up handles both flows). Smooth horizontal expand under the navbar with consistent top padding across breakpoints.",
      },
      {
        type: "improved",
        title: "Manage Hiring Heading",
        description:
          "Owner /hiring renamed from 'Hiring' to 'Manage Hiring' so it doesn't get confused with the public Job Board. Owner-mode users with zero shops also see a Create-Shop CTA instead of the empty Job Board.",
      },
      {
        type: "improved",
        title: "Shop Dashboard Polish",
        description:
          "Dropped the cover-image hero so content lifts to the top of the viewport. Stripe Connected card moved to a white-with-hairline style; its dismiss state now persists immediately via lazy-init from localStorage instead of waiting for a save round-trip. Owner home's barbers-count badge now reads through the cover with backdrop-blur-md.",
      },
      {
        type: "improved",
        title: "Add New Shop Card Refresh",
        description:
          "Owner-home Add New Shop card aligned to the greeting's left edge on desktop (no more lg:px-4 inset), taller on mobile, softer radius across breakpoints, and grows to fill the remaining viewport so the dashboard reads as one composition.",
      },
      {
        type: "improved",
        title: "View Profile Button (Premium Polish)",
        description:
          "Black bg, green User icon in a tinted badge, soft shadow + subtle inner ring, white text. Same shape on every surface the button appears (network grid, talent cards, hiring inquiries) so the action feels consistent.",
      },
      {
        type: "improved",
        title: "/barbers/[slug] Alignment",
        description:
          "Back link sits at the left edge, content body uniformly inset (pl-6 / lg:pl-8), heading reads black on white. Profile shifts right to align with the 'to' in 'Back to Network' — small detail, big difference once you see it.",
      },
      {
        type: "improved",
        title: "/listings White Container",
        description:
          "Browse Shops content sits inside a fully rounded inset container (rounded-[2.5rem] with mx-4 / lg:mx-6 / mb-8) so the rounded corners visibly breathe instead of clipping the viewport edge.",
      },
      {
        type: "improved",
        title: "Account Settings Compaction",
        description:
          "Smaller compact buttons left-aligned (Update Password, Sign Out), text scale dropped to text-[13px], max-w-xl on the form, space-y-4 vertical rhythm. The sticky Save lives inside the content row instead of floating off to the right.",
      },
      {
        type: "improved",
        title: "Rewards Cleanup",
        description:
          "Dropped the sparkles icon from the launching-soon banner. Hid the blurred mock category cards so only real rewards render. Removed tier badges from cards pending a proper tier-system design pass.",
      },
      {
        type: "improved",
        title: "Display-Heading Descender Fix",
        description:
          "leading-[1.1] across stacked display headings — descenders (g, j, p, q, y) no longer clip into the line below. Matches the BOOTH descender-clipping rule (no leading-none / leading-[0.95] on h1/h2/h3).",
      },
      {
        type: "improved",
        title: "Messages Refinements",
        description:
          "Chat container grew ~30% on desktop (drops lg:h-[85dvh] cap), conversation list radius cleaned up, selection state more visible. Drops the backdrop tint that was washing out the panel.",
      },
      {
        type: "improved",
        title: "Tablet Chrome Unified",
        description:
          "Tablet (md) now adopts the desktop chrome instead of splitting between mobile and desktop layouts — single nav source of truth across breakpoints, no more double-stacked navbars at iPad width.",
      },
      {
        type: "improved",
        title: "Booth Options Price Color",
        description:
          "Booth-plan price uses the project's dark green (#0E542B) — same token as the Hiring ACTIVE badge and the booking flow's completed-step badge. Prices visually rhyme across the product.",
      },
      {
        type: "fixed",
        title: "/hiring/[slug] for Signed-In Users",
        description:
          "Public job board detail page now uses bg-background (matches the dashboard) instead of a stark white, and PublicNavbar no longer flashes for signed-in users while auth resolves.",
      },
      {
        type: "fixed",
        title: "Mobile Map Free-Scroll",
        description:
          "Dropped the sticky top-0 on the map pane on mobile — the map now scrolls naturally with the page instead of getting pinned to the top of the viewport.",
      },
    ],
  },
  {
    date: "May 8, 2026",
    items: [
      {
        type: "new",
        title: "Hiring Module — End to End",
        description:
          "Shipped /hiring as a first-class surface. Owners can post and manage open roles, every shop's public profile gets an Open Positions section, and the public /hiring browse page launches with map-first UX, filter pills, a Filters panel, and role detail pages with an Inquire flow. Same silky map UX as /listings (drag-to-resize + scroll-driven). Owner-mode users see a manage-view (their listings only); barbers see the full job board.",
      },
      {
        type: "new",
        title: "Landing Page Hiring Section",
        description:
          "Added a Hiring landing block between the hero and Featured Listings on the home page. Open Beta Community moved to position 3 — Hiring is the new second-impression beat for first-time visitors.",
      },
      {
        type: "new",
        title: "Site-wide Mobile FloatingNav",
        description:
          "Replaced page-specific mobile menus with a single FloatingNav component that renders public / owner / barber variants. Inline account card + role-switcher in the popout, account row above the Switch container, chevron carries the role-switch cue (no separate Switch pill). Clears every page below md.",
      },
      {
        type: "new",
        title: "Desktop Navigation Pill",
        description:
          "Top-left floating pill + center-expand modal for desktop chrome. Drop-down flyout under the pill (no backdrop blur), bigger pill, gutter-centered wordmark, modal anchors recomputed for the new pill geometry. Renders on every authenticated page.",
      },
      {
        type: "new",
        title: "Notifications System",
        description:
          "Rebuilt notifications with a drop-down sheet on mobile (full-screen takeover above the FloatingNav) and a slide-from-right panel on desktop. Bell icon bumped 20%, deeper panel radius, snappier transitions, notification peek above FloatingNav. Mark-all-read uses the same chip styling as the rest of the dashboard.",
      },
      {
        type: "improved",
        title: "Authenticated Page Heading Protocol",
        description:
          "Every authenticated page heading now uses the same font, size, weight, and x-position (lg:pl-32). Headings on internal-scroll pages now scroll out cleanly with the content instead of pinning. End of the era where /listings, /hiring, /messages, /account, /rewards all had subtly different heading positions.",
      },
      {
        type: "improved",
        title: "Messages Visual Refresh",
        description:
          "Emerald send button, deeper panel radius, lifted input, dropped the gray strokes around conversation panels, rounded the panels. /messages now feels like the rest of the dashboard instead of a separate surface.",
      },
      {
        type: "improved",
        title: "Auth-aware Landing CTAs",
        description:
          "Homepage CTAs respect signed-in state — signed-in users no longer hit the signup wall when they tap an action. The Auth modal also resyncs its internal mode to defaultMode every time it opens (Sign Up CTA actually opens Sign Up, not whatever was shown last).",
      },
      {
        type: "improved",
        title: "Three-dot Loader",
        description:
          "Site-wide centered three-dot loader in #696969 replaces the half-dozen different spinners we had floating around. InlineLoader variant for section-level spinners (e.g. inside a card while data streams in).",
      },
      {
        type: "improved",
        title: "Community Floating Launcher",
        description:
          "/community gets a PlayStation-style menu — glassy floating launcher, bottom-anchor popout, lighter backdrop, naked tiles with smaller labels. Sticky view toggle below the navbar (after a small CSS fix: body overflow-x hidden → clip so sticky positioning actually works).",
      },
      {
        type: "improved",
        title: "Owner-mode /hiring as Manage View",
        description:
          "Owners visiting /hiring see their own listings only — a true manage view — instead of the public Job Board. Role switcher still lets them flip to the public Job Board if they want to see what barbers see.",
      },
      {
        type: "fixed",
        title: "iOS Status-Bar Strip",
        description:
          "theme-color flipped from #000 to #F7F7F7 so the iOS status bar no longer renders as a dark strip above the white app surface.",
      },
      {
        type: "fixed",
        title: "Barber-Mode Create-Shop Wizard",
        description:
          "Block the Create-Shop wizard entirely when a user is in Barber mode — previously barbers could reach the form in some flows even though they couldn't submit it.",
      },
      {
        type: "fixed",
        title: "/community Signed-in Signup CTA",
        description:
          "Create-account CTA no longer renders for users who are already signed in.",
      },
    ],
  },
  {
    date: "May 7, 2026",
    items: [
      {
        type: "improved",
        title: "Community Feed Auth Gate",
        description:
          "Upvote and comment clicks in the community feed now open the signup modal for logged-out users instead of failing silently. Lowers the friction between 'I want to engage' and 'I have an account'.",
      },
    ],
  },
  {
    date: "May 6, 2026",
    items: [
      {
        type: "new",
        title: "Booking Platforms Landing Section",
        description:
          "Added a feature-reveal section on the landing page that calls out the booking platforms BOOTH connects to. Drag-to-rearrange pills, glossy off-black background, NEW badge — and a 60svh cap on desktop so it reads as a beat, not a wall.",
      },
      {
        type: "new",
        title: "Platform-branded Booking Links",
        description:
          "Barber and shop profiles can now surface platform-branded booking links (Square, Booksy, GlossGenius, Schedulicity, etc.) under Links. Booking logo renders first and ~33% bigger so it's the obvious next action.",
      },
      {
        type: "new",
        title: "Supply Pipeline Metrics View",
        description:
          "shop_supply_metrics SQL view captures the funnel from interest → listed → bookable, so we can read supply-side health without joining 4 tables in the console.",
      },
      {
        type: "improved",
        title: "OG Share Image",
        description:
          "Proper 1200×630 share thumbnail generated with a white background + black BOOTH press logo. Links shared in Messages, Slack, and SMS now render the real brand mark instead of the cropped favicon.",
      },
      {
        type: "improved",
        title: "Inquire Modal Polish",
        description:
          "White BOOTH logo at the top of the Inquire modal, green focus ring killed (was fighting the dark theme of the modal surface).",
      },
      {
        type: "fixed",
        title: "Booking Logos Render",
        description:
          "Switched booking-platform logos from next/image to a plain <img> so the SVG sources render correctly. Client-side URL fallback added so legacy rows that haven't been migrated still light up with their platform mark.",
      },
    ],
  },
  {
    date: "May 5, 2026",
    items: [
      {
        type: "fixed",
        title: "Inquire Button on Barber Profiles",
        description:
          "The Inquire button on a barber's public profile now opens the same dialog used in /dashboard/owner/network — owners can write a personal note and optionally attach a booth plan instead of firing a generic message silently. The button stays owner-only; barbers viewing other barbers see Message only.",
      },
      {
        type: "improved",
        title: "Inquire Modal Dark Theme",
        description:
          "Rebuilt the invite dialog with the auth signup palette (#111111 surface, #0a0a0a inputs, white text), tighter heading hierarchy with uppercase labels, rounded-3xl corners, and a hairline-separated footer. Long shop names no longer clip behind the close X, and pill plan toggles invert to white-on-black when selected.",
      },
      {
        type: "improved",
        title: "Portfolio Image Lightbox",
        description:
          "Tapping a portfolio image no longer drops you into a full black void. The image now floats centered with a rounded-2xl frame and a soft, translucent backdrop blur — same radius as the portfolio cards, and applies to both owner and barber views of a profile.",
      },
    ],
  },
  {
    date: "May 4, 2026",
    items: [
      {
        type: "new",
        title: "Portfolio View Modes",
        description:
          "Barber profile portfolios now offer three view modes — 3-up grid, 2-up grid, and full-width single column — toggleable from a small pill above the gallery.",
      },
      {
        type: "improved",
        title: "Auth Stroke + Iconography Refresh",
        description:
          "Unified, sharper icon strokes across the AuthModal, /auth/signup, and /auth/login so the three entry points feel like one consistent surface.",
      },
      {
        type: "improved",
        title: "Community Heading & Layout",
        description:
          "Bigger heading and a wider container on /community so the desktop layout fills the page properly.",
      },
      {
        type: "improved",
        title: "Barber Network Scroll on Desktop",
        description:
          "/dashboard/barber/network now scrolls at the page level on desktop and renders larger posts so the feed reads more like a magazine and less like a card list.",
      },
      {
        type: "improved",
        title: "Follow / Share Split Pill",
        description:
          "Unified the Follow/Share pill across /barbers/[slug] and owner network talent cards — white background, very-light gray border + divider, follow icon stays emerald, matched 26px icon buttons.",
      },
      {
        type: "improved",
        title: "Barber Profile Vertical Rhythm",
        description:
          "Tightened spacing between action icons, the badge row, stats, Message/Inquire, and Links by ~33% (24px → 16px) so the profile reads in a single glance.",
      },
    ],
  },
  {
    date: "May 3, 2026",
    items: [
      {
        type: "improved",
        title: "Instant Skeleton Loaders for Community ↔ Profile",
        description:
          "Tapping a barber card on /community now paints a polished skeleton instantly while the profile streams in, and the back-out feels just as snappy. The skeleton sits in the same column as the real profile, so content no longer jumps when streaming finishes.",
      },
      {
        type: "fixed",
        title: "Mobile Hero Layout",
        description:
          "Reworked the home hero so the Community and Empty Chairs sections fit cleanly inside one mobile viewport, with a full-bleed Empty Chairs panel underneath.",
      },
    ],
  },
  {
    date: "May 2, 2026",
    items: [
      {
        type: "new",
        title: "Followers Modal",
        description:
          "Tap the followers count on a barber profile to open a modal listing everyone who follows them, with quick links to each profile.",
      },
      {
        type: "new",
        title: "Notifications for Upvotes & Comments",
        description:
          "Barbers now get an in-app notification when someone upvotes or comments on one of their community posts, so feedback loops happen in real time.",
      },
      {
        type: "improved",
        title: "Required Headline at Onboarding",
        description:
          "Replaced the optional Bio textarea with a required Headline (e.g. 'Skin fades & beard work · 5 yrs · Brooklyn') so every new profile lands on /community and /barbers/<slug> with a clear, scannable intro.",
      },
      {
        type: "improved",
        title: "Community Feed — Trending First, No Back-to-Back Barbers",
        description:
          "Trending is now the default sort on both barber and owner network pages. The feed de-clusters posts so you never see two from the same barber in a row, and Recent now leads with the latest upload pinned to the top-left, then round-robins through everyone else.",
      },
      {
        type: "improved",
        title: "Public Community Polish",
        description:
          "Added a centered '+ Create account' CTA below the public feed (same modal as the header), tightened mobile spacing between the header and the grid, and grouped Browse + Community closer on the tablet navbar.",
      },
      {
        type: "improved",
        title: "Auth Modal & Role Pills Refresh",
        description:
          "Black-themed signup modal with white/translucent inputs, inverted role pills, and a high-contrast white CTA. Role pills (Barber / Owner) now look identical between the modal and the full /auth/signup page — black fill, thin white outline, white text.",
      },
      {
        type: "improved",
        title: "Home Hero Refresh",
        description:
          "Home hero typography now mirrors /community — light 'Community' headline over a medium 'Network', a quieter Update pill, and a minimal '+ Create account'-style CTA. The globe scales to fit the section vertically on mobile and the Community panel got a much shorter mobile height.",
      },
      {
        type: "improved",
        title: "Pricing Page Typography",
        description:
          "Split 'Simple Pricing, Ultimate Value' into the same Community/Network typographic pair used elsewhere, and swapped the subline for mission-aligned copy: 'Built to power a more connected and visible barbershop ecosystem.'",
      },
      {
        type: "improved",
        title: "Rewards Page Polish",
        description:
          "Rewards hero h1 now mirrors the /community heading style and stays on two lines on mobile. The dashboard home now shows the same Available Points + tier + progress + benefits + How to Earn card as /dashboard/barber/rewards, and the Star icon was dropped from tier badges everywhere for a calmer look.",
      },
      {
        type: "improved",
        title: "My Booths Empty State",
        description:
          "Recolored the empty My Booths card on the barber dashboard to a black/translucent design with thin glyphs and a points pill. Tapping it now opens the Invite Shop Owner modal — copyable owner sign-up link plus mailto/sms quick-shares — instead of routing out to /listings.",
      },
      {
        type: "improved",
        title: "Owner Dashboard Empty State",
        description:
          "The 'Add New Shop' card now fills the remaining mobile viewport, lists every shop type ('barbershop, studio, salon, or suite'), and uses a larger BOOTH glyph for a more confident first impression.",
      },
      {
        type: "improved",
        title: "Network Profile Layout Polish",
        description:
          "On desktop, the feed is locked next to a pinned profile column with cleaner gaps (16px → 32px → 64px depending on density). The profile summary card lost its border and white fill so it sits flush with the page background, and the 'Open to work' toggle row now aligns with the profile card padding above it.",
      },
      {
        type: "improved",
        title: "Sidebar & Mobile Menu Polish",
        description:
          "Desktop sidebar Sign Out hover now only tints the text + icon red (no red background slab), and the border above the account block is gone. The user/role pill at the top of the mobile slide-out lost its border in both barber and owner modes.",
      },
      {
        type: "improved",
        title: "Thinner Profile Badges",
        description:
          "Open to work, Part-time, Established clientele, Booth rental, and similar colored badges on barber profiles now use thinner weight and slightly smaller text for a more refined look.",
      },
      {
        type: "improved",
        title: "Account Settings Copy",
        description:
          "The Switch Mode block on /dashboard/account now reflects the new BOOTH Community update.",
      },
      {
        type: "improved",
        title: "Network Edit Copy",
        description:
          "Renamed the Shop Experience empty-state CTA from 'Add your first shop' to 'Add your work experience' so it reads as career history instead of pushing a listing.",
      },
      {
        type: "fixed",
        title: "Owner Community CTA on Mobile",
        description:
          "Hid the mobile 'Set up your shop' CTA while the owner community feed is still loading so it no longer flashes in and out as the page hydrates.",
      },
    ],
  },
  {
    date: "April 30, 2026",
    items: [
      {
        type: "new",
        title: "Inquire Modal for Owners",
        description:
          "Shop owners can now reach out to barbers with a personal note and optionally attach a booth plan — pricing, cadence, and session count travel with the message. Replaces the auto-generated 'I'd love to chat' template with something owners can shape.",
      },
      {
        type: "new",
        title: "Per-Image Votes & Comments",
        description:
          "Upvotes, downvotes, and comments now scope to a single feed post instead of spreading across every photo in a barber's portfolio. Each piece of work gets its own conversation.",
      },
      {
        type: "new",
        title: "Delete Your Comments",
        description:
          "A 3-dot menu next to your own comments now lets you delete them inline. Counts update instantly.",
      },
      {
        type: "new",
        title: "Share Profile",
        description:
          "Added a Share button beneath the avatar on barber profiles. Uses the native iOS / Android share sheet, with a clipboard-copy fallback on desktop. Share previews always render with BOOTH branding so links look polished in iMessage, X, and LinkedIn.",
      },
      {
        type: "new",
        title: "Shop Experience Linking",
        description:
          "When a barber's past shop is on BOOTH, that row in their Shop Experience now shows the real shop logo and links to the shop's public listing. Editing the field also autocompletes shops in our system.",
      },
      {
        type: "improved",
        title: "Toast Notifications Across the App",
        description:
          "Confirmations, errors, and success states now actually appear when you take an action. The toast provider was missing app-wide; mounting it surfaced a couple of silent failures we caught and fixed in this same batch.",
      },
      {
        type: "improved",
        title: "Barber Profile Hierarchy",
        description:
          "Bigger avatar, a new '15Y EXP' stat pill leading the stats row, follow + share controls collapsed into a single chip beneath the header, and a green check when you're already following.",
      },
      {
        type: "improved",
        title: "Hiring Badges Are Owner-Only",
        description:
          "Open to Work, availability, clientele size, and payment preference badges now only show to shop owners viewing a barber profile. Barber viewers see a cleaner profile without the hiring-signal noise.",
      },
      {
        type: "improved",
        title: "X Replaces Facebook on Profiles",
        description:
          "Swapped Facebook for X (formerly Twitter) on barber profile socials and the edit form. Update your link from Settings → Network Profile → Socials.",
      },
      {
        type: "improved",
        title: "Owner Network Polish",
        description:
          "Message and Inquire now read as a quiet pair of icon links — no more bordered pill on Message. Native gray scrollbars in the column scroll surfaces are hidden for a cleaner look.",
      },
      {
        type: "improved",
        title: "Network Onboarding Redesign",
        description:
          "The 'Join the BOOTH Network' screen got a lighter outline icon (no black tile), a globe icon for Discovered, a palette icon for Portfolio, and tighter card copy so nothing wraps to three lines.",
      },
      {
        type: "improved",
        title: "Barber Shop Page Hero & Layout",
        description:
          "Hero image now sits as a fully rounded card with side margins matching the content below. Active Member status moved beneath Team Roster with a yellow Verified pastel pill, and Booth Rental Options now sit between Team Roster and My Schedule.",
      },
      {
        type: "improved",
        title: "Account Settings Layout",
        description:
          "Mode badge restyled to black with a thin scissors / store glyph. Switch Mode now sits above Edit Details so the Profile / Billing / Notifications / Security tabs lead the page.",
      },
      {
        type: "improved",
        title: "Mobile Portfolio Carousel",
        description:
          "Portfolio carousels on mobile now bleed past the page padding so images can scroll edge-to-edge while keeping the first one visually inset. Same recipe applied across all carousels.",
      },
      {
        type: "improved",
        title: "Listings Page Navbar Reliability",
        description:
          "Browse Listings now reliably renders the right navbar at every viewport, including the tablet range that previously slipped between mobile and desktop layouts.",
      },
      {
        type: "removed",
        title: "Save Button on Barber Profiles",
        description:
          "Barbers don't save other barbers — removed the lingering Save action from public barber profiles to match how the feature actually works.",
      },
      {
        type: "removed",
        title: "Inline 'My Profile' on Network",
        description:
          "The /dashboard/barber/network page used to show a separate, slightly-different version of your profile. Clicking your avatar in the feed now opens the canonical, polished public profile at /barbers/your-slug — one source of truth.",
      },
    ],
  },
  {
    date: "April 28, 2026",
    items: [
      {
        type: "new",
        title: "BOOTH Network",
        description:
          "Launched BOOTH Network — a public barber directory and community feed. Barbers get a public profile at /barbers/your-slug with portfolio, shop history, socials, booking link, and an Open to Work badge. Shop owners can browse, follow, and reach out directly.",
      },
      {
        type: "new",
        title: "Per-Image Feed Cards",
        description:
          "The Network feed now shows one card per portfolio image instead of one per barber, giving every photo its own moment. Cards are 70svh on mobile so the next post peeks on scroll. Recent and Trending sorts live in the feed header.",
      },
      {
        type: "new",
        title: "Upvote / Downvote + Follow",
        description:
          "Vote on barber feed posts and follow profiles directly from each card. Trending sort uses a blend of net votes and follower count.",
      },
      {
        type: "new",
        title: "Post to the Feed",
        description:
          "A + Post button in the Barber Feed opens an upload modal — instant preview, optional caption, and the post appears in the feed and your portfolio when you publish.",
      },
      {
        type: "new",
        title: "Public Barber Profile Redesign",
        description:
          "Public profiles now use a single-column layout on mobile with Profile / Portfolio tabs and a two-column reference layout on desktop. Portfolio supports a carousel and grid toggle, and edit screens auto-scroll to the right section.",
      },
      {
        type: "new",
        title: "Owner Network Dashboard",
        description:
          "Shop owners get their own Network page to scout barbers — same feed, same filters, with action buttons tailored to outreach.",
      },
      {
        type: "new",
        title: "Sessions Live on the Dashboard",
        description:
          "Upcoming sessions now appear directly on the Barber Dashboard between My Booths and Payment Activity — including reschedule and cancel actions and a friendly empty state. The standalone Sessions nav item has been removed.",
      },
      {
        type: "new",
        title: "First Shop Bonus — 250 Points",
        description:
          "Barbers earn a one-time 250-point bonus the first time they join a shop on BOOTH. Surfaced as an earnable card on the rewards page and on the empty My Booths state.",
      },
      {
        type: "improved",
        title: "Add Your First Shop Card",
        description:
          "When a barber has no shops yet, the dashboard now shows a premium Add Your First Shop card with a warm amber gradient, a points pill, and a soft Building glyph — replacing the dashed empty state.",
      },
      {
        type: "improved",
        title: "Network Layout Swap",
        description:
          "On desktop, the Barber Feed now sits on the left at full width with the profile column on the right at ~3.3x narrower for a cleaner reading flow. Open to Work moves outside the profile card as its own row, and the always-on Hide Profile toggle was removed — profiles are visible in the directory once published.",
      },
      {
        type: "improved",
        title: "Profile Card View / Edit",
        description:
          "On the Network profile, Edit becomes a small text link aligned with the avatar row, and View Profile is now a solid black button with a profile icon.",
      },
      {
        type: "improved",
        title: "Portfolio Upload",
        description:
          "Drag-to-reorder, instant local previews, and image processing on upload. Portfolio section now sits above Socials and Shop Experience in the edit profile flow.",
      },
      {
        type: "improved",
        title: "Network Profile Draft Persistence",
        description:
          "Network profile edits are saved to localStorage so a tab switch or accidental refresh no longer wipes your progress.",
      },
      {
        type: "fixed",
        title: "Public Profile Navigation",
        description:
          "Public barber profile pages now include the dashboard sidebar on desktop and a back button on mobile, with proper z-indexing on the back bar so it never gets clipped.",
      },
    ],
  },
  {
    date: "April 27, 2026",
    items: [
      {
        type: "new",
        title: "Pro Plan — Coming Soon",
        description:
          "Introduced Starter (free) and Pro ($75/qtr or $250/yr) plans on the pricing page. Pro billing is paused for an open beta — current shops keep the full feature set free until launch.",
      },
      {
        type: "new",
        title: "3% Platform Fee",
        description:
          "All booth rental and booking payments now include a 3% platform fee on top of Stripe processing. The fee model ensures BOOTH never pays processing fees, and barbers see the breakdown clearly at checkout.",
      },
      {
        type: "improved",
        title: "Pricing Badges Refined",
        description:
          "Refined pricing badges across Starter and Pro cards — thinner, more premium feel. ALWAYS is green, UNLIMITED is soft yellow, and the Pro top badge is black with white text. COMING SOON also goes black-on-white for a clean, neutral signal.",
      },
      {
        type: "improved",
        title: "Public Pricing Page",
        description:
          "Removed the password lock from the pricing page so anyone can view it directly. Fixed copy to clarify owners receive 100% of barber rent — not 97%.",
      },
      {
        type: "fixed",
        title: "Heading Clipping on Mobile",
        description:
          "Pricing page headings now scale down on small screens (text-3xl mobile, text-4xl tablet, text-5xl desktop) so titles never clip.",
      },
    ],
  },
  {
    date: "April 5, 2026",
    items: [
      {
        type: "fixed",
        title: "Owner Dashboard Mobile Padding",
        description:
          "Added top padding on the owner dashboard so the welcome section clears the fixed mobile navbar.",
      },
    ],
  },
  {
    date: "April 2, 2026",
    items: [
      {
        type: "new",
        title: "Barber Detail Modal",
        description:
          "Click any barber on the shop roster to open a detail modal showing their payment status, contact info, plan details, earnings from that barber, and the ability to send a plan offer or payment reminder.",
      },
      {
        type: "new",
        title: "Payment Status Badges",
        description:
          "Each barber on the owner's roster now displays a color-coded payment badge — Recurring (green), On Plan (blue), Payment Pending (amber), Cash (gray), or No Plan. Owners can tap Remind to nudge barbers who haven't completed payment setup.",
      },
      {
        type: "new",
        title: "Payment Reminder Notifications",
        description:
          "Automated daily cron job sends notifications to barbers and shop owners 1 day before and on the day of payment. Covers both Stripe and cash payments with deduplication to prevent spam.",
      },
      {
        type: "new",
        title: "Explore for Owners",
        description:
          "Added Explore to the owner sidebar and mobile menu so shop owners can browse other listings on the platform. Heart/save button hidden for owners since it's a barber feature.",
      },
      {
        type: "improved",
        title: "Auto-Pay Defaults to On",
        description:
          "The auto-pay toggle during barber checkout now defaults to on with green BOOTH branding and a Recommended badge. Barbers can still opt out, but the default nudges toward recurring payments.",
      },
      {
        type: "improved",
        title: "Projected Income Accuracy",
        description:
          "Projected Income on the owner dashboard now calculates from actual accepted plan offers instead of multiplying roster count by average plan price. Shows real expected revenue.",
      },
      {
        type: "improved",
        title: "Stripe Connect Banner Redesign",
        description:
          "Restyled the Connect Stripe banner on the owner dashboard with official Stripe purple branding, inline S logo, and matching button. Balance text updated to Stripe purple.",
      },
      {
        type: "improved",
        title: "Listing Views Now All-Time",
        description:
          "Listing views on the owner dashboard now show all-time totals instead of resetting monthly.",
      },
      {
        type: "improved",
        title: "Owner Messaging Empty State",
        description:
          "Owners now see 'When barbers are interested in your shop, their messages will appear here' instead of the barber-facing 'Contact a shop owner' message.",
      },
      {
        type: "fixed",
        title: "Account Settings Back Button",
        description:
          "Fixed the back button on Account Settings routing owners to the barber dashboard. Now correctly uses URL-based role detection.",
      },
      {
        type: "fixed",
        title: "Account Settings Top Bar Alignment",
        description:
          "The Account Settings header bar now respects the sidebar width on desktop instead of stretching under it.",
      },
      {
        type: "fixed",
        title: "Password Reset Page Logo",
        description:
          "Replaced the placeholder SVG logo on forgot-password and reset-password pages with the official BOOTH logo.",
      },
      {
        type: "improved",
        title: "Clean Listing URLs",
        description:
          "Shop listing links now use readable slugs like /listings/abs-workspace instead of long UUIDs.",
      },
      {
        type: "improved",
        title: "Homepage Feature Section",
        description:
          "Darkened the Owners/Barbers tab toggle to match the Sign Up button. Increased feature card text sizes for better readability.",
      },
      {
        type: "fixed",
        title: "Orphan Word Fix",
        description:
          "Fixed single words appearing alone on a line in the Building in Public subtitle on mobile.",
      },
    ],
  },
  {
    date: "April 1, 2026",
    items: [
      {
        type: "new",
        title: "Save Changes Button on Edit Shop",
        description:
          "Added a full-width Save Changes button as the primary CTA at the bottom of the edit shop page. Delete Shop moved to a small inline text link below.",
      },
      {
        type: "new",
        title: "View All Photos on Mobile",
        description:
          "Mobile shop listing pages now have a small View All button inside the image gallery that opens the full-screen photo lightbox.",
      },
      {
        type: "improved",
        title: "Amenity Selector Redesign",
        description:
          "Rebuilt the amenity selector as minimal flat pill toggles. Removed category accordions — all amenities flow in one section. Custom amenities use a tag icon instead of sparkles.",
      },
      {
        type: "improved",
        title: "Create Shop Upload Interactions",
        description:
          "All upload areas on Create New Shop now have hover effects — logo, cover image, and gallery photos. Gallery images support drag-to-reorder with position numbers.",
      },
      {
        type: "improved",
        title: "Map Zoom & Badge Controls",
        description:
          "Map zoom buttons now always visible on all screen sizes. Added a shop count badge on the map. Removed unused Leaflet control containers.",
      },
      {
        type: "improved",
        title: "Mobile Shop Page Reorder",
        description:
          "Moved the Questions/Contact card above the Location section on mobile for better flow. Reduced image gallery height by 10%.",
      },
      {
        type: "improved",
        title: "Listings Page Performance",
        description:
          "Lazy loading on carousel images, HTTP cache headers on the shops API, removed per-listing console.logs from map, and added preconnect to Supabase storage domain.",
      },
      {
        type: "improved",
        title: "Dashboard Welcome Spacing",
        description:
          "Reduced excessive top padding on both owner and barber dashboard welcome sections so the greeting aligns with the sidebar navigation.",
      },
      {
        type: "fixed",
        title: "Map Price Badge Centering",
        description:
          "Map price badges now center correctly regardless of text width. Removed the /mo suffix — badges just show the price.",
      },
      {
        type: "fixed",
        title: "Carousel Dots Position",
        description:
          "Moved the image carousel dots on listing cards down to align with the city line instead of overlapping the shop name.",
      },
      {
        type: "fixed",
        title: "Create Shop Whitespace Bug",
        description:
          "Fixed a ghost whitespace block on the Create New Shop page caused by min-h-screen and excessive bottom padding.",
      },
      {
        type: "fixed",
        title: "Logo Remove Button Clipping",
        description:
          "Fixed the red X button on the shop logo being clipped inside the circular image instead of overlapping the corner.",
      },
      {
        type: "fixed",
        title: "Booth Options Metric Removed",
        description:
          "Removed the confusing available/total barber metric from the Booth Options header on shop listing pages.",
      },
      {
        type: "fixed",
        title: "Demo Shop Hidden from Listings",
        description:
          "Barbershop Demo no longer appears in public Browse Listings. Still accessible via direct URL for testing.",
      },
    ],
  },
  {
    date: "March 30, 2026",
    items: [
      {
        type: "new",
        title: "Building in Public Page",
        description:
          "Renamed the Changelog to Building in Public with two tabs — Changelog for product updates and Founder's Journal for behind-the-scenes entries. Added minimal underline tab navigation.",
      },
      {
        type: "new",
        title: "Founder's Journal",
        description:
          "Launched a founder's journal with tagged entries (Conversation, Idea, Partnerships, Milestone, Event, Lesson, Community). First entries are live.",
      },
      {
        type: "improved",
        title: "Building in Public Navbar",
        description:
          "Added a dark-themed navbar to the Building in Public page with Browse, Rewards, Log In, and Sign Up links matching the page's aesthetic.",
      },
      {
        type: "improved",
        title: "Homepage SEO Overhaul",
        description:
          "Refined all metadata to position BOOTH as a barbershop discovery platform and double-sided marketplace. Added competitor alternative keywords and hidden SEO comparison sections.",
      },
      {
        type: "fixed",
        title: "Listings Map Fix",
        description:
          "Fixed the map failing to load on the Browse page. A dead code reference to a removed zoom control was crashing map initialization.",
      },
    ],
  },
  {
    date: "March 29, 2026",
    items: [
      {
        type: "fixed",
        title: "Revenue Analytics Fix",
        description:
          "Fixed the Revenue YTD percentage on the owner dashboard. The comparison was incorrectly using full year-to-date against a single month, causing inflated percentages. Now correctly compares this month vs last month.",
      },
      {
        type: "new",
        title: "Fullscreen Map on Desktop",
        description:
          "Added a fullscreen map option on the desktop listings page. Click Expand Map to browse listings on a full-view dark map with price markers.",
      },
      {
        type: "improved",
        title: "Map UX Overhaul",
        description:
          "Rebuilt map interactions — custom React zoom buttons, modern price badges, card highlighting when clicking map markers, popup cards in fullscreen mode, and fixed stale closure bugs.",
      },
      {
        type: "new",
        title: "Projected Income Card",
        description:
          "Added a Projected Income card with a green pulse indicator to the owner dashboard, giving shop owners a forward-looking revenue estimate.",
      },
      {
        type: "improved",
        title: "Owner Dashboard Metrics",
        description:
          "Reworked Occupancy to show booths filled instead of barber count, and fixed Avg Per Barber to use current month earnings. Capped revenue percentage to show 'Strong growth' when over 999%.",
      },
      {
        type: "improved",
        title: "Session Time Slot Badges",
        description:
          "Updated session time slot badges to a cleaner black, white, and gray palette.",
      },
      {
        type: "improved",
        title: "Homepage Typography & Layout",
        description:
          "Increased feature section text size and fixed orphan words on headings across the site.",
      },
      {
        type: "improved",
        title: "Privacy Policy & Terms of Service",
        description:
          "Rewrote both the Privacy Policy and Terms of Service with updated, clearer language.",
      },
      {
        type: "fixed",
        title: "Auth Page Centering",
        description:
          "Fixed vertical centering on both the sign in and sign up pages by removing excess padding.",
      },
      {
        type: "fixed",
        title: "Blog Header Fix",
        description:
          "Fixed the Blog page header where the descender on the letter 'g' was being clipped.",
      },
      {
        type: "fixed",
        title: "Instagram Link Corrected",
        description:
          "Fixed the Instagram link in the open beta community section to point to the correct @booth__app account.",
      },
    ],
  },
  {
    date: "March 27, 2026",
    items: [
      {
        type: "new",
        title: "Founder Photo on Press Page",
        description:
          "Added a full-frame founder photo to the press page, replacing the placeholder.",
      },
      {
        type: "new",
        title: "Downloadable Deck (2025)",
        description:
          "The BOOTH deck is now available for download directly from the press page alongside the founder photo and logo assets.",
      },
      {
        type: "improved",
        title: "Press Page Copy Refresh",
        description:
          "Rewrote the About BOOTH and Founder Bio sections with updated copy. Refined messaging around the marketplace positioning and founder background.",
      },
      {
        type: "fixed",
        title: "Logo Light Background Match",
        description:
          "Fixed the Logo Light download card background to exactly match the logo image, removing the visible color mismatch.",
      },
    ],
  },
  {
    date: "March 22, 2026",
    items: [
      {
        type: "improved",
        title: "App Font Upgrade",
        description:
          "Switched the entire platform font from Inter to Geist for a cleaner, more modern look across all pages.",
      },
      {
        type: "new",
        title: "Open Beta Community Section",
        description:
          "Added a new community section to the landing page with floating social icons linking to Facebook, Instagram, LinkedIn, TikTok, Threads, and Discord.",
      },
      {
        type: "improved",
        title: "Rewards Page Reorder",
        description:
          "Reorganized the public rewards page layout: partners preview and login CTA now sit above the tiers and how-to-earn sections for better flow.",
      },
      {
        type: "fixed",
        title: "Social Links Corrected",
        description:
          "Fixed LinkedIn, TikTok, and Facebook URLs across the site to point to the correct BOOTH profiles.",
      },
    ],
  },
  {
    date: "March 21, 2026",
    items: [
      {
        type: "improved",
        title: "Listings Page Redesign",
        description:
          "Responsive grid layout with 1 to 4 columns depending on screen size. Improved search bar styling and filter layout for desktop.",
      },
      {
        type: "improved",
        title: "Map Zoom Controls",
        description:
          "Updated zoom controls to a cleaner circle style on both the listings map and listing detail maps.",
      },
    ],
  },
  {
    date: "March 20, 2026",
    items: [
      {
        type: "new",
        title: "Downloadable Logo Assets",
        description:
          "Added BOOTH logo downloads to the press page with preview cards showing both dark and light variants.",
      },
    ],
  },
  {
    date: "March 19, 2026",
    items: [
      {
        type: "new",
        title: "Public Rewards Page",
        description:
          "Launched a public-facing rewards page accessible from the navbar and footer. Browse partner previews, reward tiers, and how to earn before signing in.",
      },
      {
        type: "improved",
        title: "Homepage Hero Update",
        description:
          "Added Create Account and Sign In buttons to the hero section. Updated the public footer with press, traction, and social links.",
      },
      {
        type: "improved",
        title: "Desktop Listings Filters",
        description:
          "Split filters and search into two rows on desktop for a cleaner browsing experience. Filters above, search below.",
      },
    ],
  },
  {
    date: "March 17, 2026",
    items: [
      {
        type: "new",
        title: "Press Page",
        description:
          "Launched /press with founder bio, live platform stats pulled from Supabase, downloadable assets, and an embedded Instagram feed. Accessible from the homepage footer.",
      },
      {
        type: "improved",
        title: "Features Section Redesign",
        description:
          "Rebuilt the features section on the homepage with a toggle between owner and barber views, compact cards, and refined spacing.",
      },
      {
        type: "improved",
        title: "Listings Page Overhaul",
        description:
          "Redesigned the listings page with a collapsible map, full-image listing cards with text overlays, unified filter bar, and mobile-optimized layout with expandable fullscreen map.",
      },
      {
        type: "new",
        title: "Email Change in Settings",
        description:
          "Users can now update their email address from account settings.",
      },
      {
        type: "improved",
        title: "Mobile Map Experience",
        description:
          "Mobile users now get split filter rows, independent scroll for types and cities, and a collapse-on-scroll map that expands to fullscreen on tap.",
      },
    ],
  },
  {
    date: "March 11, 2026",
    items: [
      {
        type: "improved",
        title: "Homepage Hero Image",
        description:
          "Swapped in a new high-resolution studio shot for the hero section. Optimized for retina displays and repositioned to show the full chair on both desktop and mobile.",
      },
    ],
  },
  {
    date: "March 2, 2026",
    items: [
      {
        type: "fixed",
        title: "Onboarding Avatar and Bio",
        description:
          "Fixed barber onboarding to properly save bio text and avatar photo URL. Added migration for avatar_url column.",
      },
      {
        type: "fixed",
        title: "Booth Plans Save",
        description:
          "Fixed booth plans failing to save by switching from delete-and-reinsert to upsert, preventing race conditions.",
      },
    ],
  },
  {
    date: "February 19, 2026",
    items: [
      {
        type: "fixed",
        title: "Signup Error Resolved",
        description:
          "Fixed a 500 error during signup caused by email confirmation flow. New users can now create accounts without issues.",
      },
      {
        type: "fixed",
        title: "Number Input Leading Zeros",
        description:
          "Fixed a bug where number fields on edit/create shop forms would display leading zeros.",
      },
      {
        type: "fixed",
        title: "Pricing Display Accuracy",
        description:
          "Filtered out placeholder booth plans so listings no longer incorrectly show $1 pricing or 0% commission.",
      },
      {
        type: "improved",
        title: "Map Price Markers",
        description:
          "Map markers now display in green for better visibility and contrast against the map background.",
      },
      {
        type: "fixed",
        title: "Listings Page Mobile Layout",
        description:
          "Resolved a white block appearing on the listings page on mobile. Page now scrolls naturally with proper background transparency.",
      },
    ],
  },
  {
    date: "February 16, 2026",
    items: [
      {
        type: "improved",
        title: "Shop Image Uploads",
        description:
          "Rebuilt image upload experience for logo, cover, and gallery photos across Create Shop and Edit Shop. Uploads now show instant previews and work reliably on all mobile browsers.",
      },
      {
        type: "improved",
        title: "Stripe Dashboard Access",
        description:
          "Improved Stripe Dashboard buttons throughout the platform. One-tap access to your Stripe account now works seamlessly on both desktop and mobile devices.",
      },
      {
        type: "improved",
        title: "Shop Preview Layout",
        description:
          "Simplified the shop preview page to a clean scroll-through layout matching the public listing experience. Removed the tabs/scroll toggle for a more consistent feel.",
      },
      {
        type: "improved",
        title: "Rewards Page Redesign",
        description:
          "Redesigned the rewards page with a cleaner, more compact layout. Streamlined the How to Earn section, updated category icons, and tightened the overall spacing for a better mobile experience.",
      },
      {
        type: "improved",
        title: "Rewards Tier Progression",
        description:
          "Adjusted reward tier thresholds so barbers can reach Silver, Gold, and Platinum status faster. Early progression now feels more achievable and rewarding.",
      },
    ],
  },
  {
    date: "February 13, 2026",
    items: [
      {
        type: "new",
        title: "Session Date Rescheduling",
        description:
          "Barbers can now change their booked session dates directly from the Sessions page. Supports single session reschedule and bulk date management for moving multiple sessions at once.",
      },
      {
        type: "improved",
        title: "Smart Schedule View",
        description:
          "Your weekly schedule now auto-populates from your booked sessions with AM, PM, and Full Day time slots. No more manual entry — book a session and it shows up on your schedule automatically.",
      },
      {
        type: "new",
        title: "Rewards Program Preview",
        description:
          "Added a preview of upcoming partner rewards across barber supplies, wellness, and travel categories. Earn points with every booth payment and redeem them for exclusive discounts soon.",
      },
      {
        type: "improved",
        title: "Rewards Card Redesign",
        description:
          "Redesigned the rewards points card with a sleek dark holographic look. Tier badge and progress bar now feel premium and less cluttered.",
      },
      {
        type: "fixed",
        title: "Payment Activity Tracking",
        description:
          "Fixed an issue where completed booth payments weren't appearing in the shop's Payment Activity section. All transactions now record and display correctly.",
      },
      {
        type: "improved",
        title: "Messaging & Offers Reliability",
        description:
          "Fixed multiple messaging bugs including real-time subscriptions, offer modal interactions, toast notifications, and pagination. Conversations are now more reliable across the board.",
      },
    ],
  },
  {
    date: "February 11, 2026",
    items: [
      {
        type: "new",
        title: "Calendar-Based Booth Booking",
        description:
          "Launched a brand-new booking system. Barbers select daily, weekly, biweekly, or monthly plans, pick specific dates on an interactive calendar with real-time availability, and pay upfront via Stripe.",
      },
      {
        type: "new",
        title: "Custom Session Slots",
        description:
          "Shop owners can now define custom session time slots (AM, PM, Full Day, or custom windows) for each booth plan. Full control over how booth time is divided.",
      },
      {
        type: "improved",
        title: "Booking Flow & Checkout",
        description:
          "Redesigned the entire booking interface with cleaner plan cards, clearer pricing, an auto-pay toggle at checkout, and a seamless path from browsing to booking.",
      },
      {
        type: "improved",
        title: "Owner Plan Editor",
        description:
          "Owners can now configure the number of sessions per plan, set session mode (AM/PM/Full Day), and define custom session hours — all from the booth plan editor.",
      },
    ],
  },
  {
    date: "February 8, 2026",
    items: [
      {
        type: "new",
        title: "Invite Barber to Your Shop",
        description:
          "Shop owners can now invite barbers directly by email or phone. Invited barbers receive a notification and can join the roster with one tap — no searching required.",
      },
      {
        type: "improved",
        title: "Mobile Responsiveness",
        description:
          "Fixed roster button layouts, invite modal sizing, and calendar controls on smaller screens. The entire owner dashboard is now fully usable on mobile.",
      },
      {
        type: "improved",
        title: "Today Highlight in Calendar",
        description:
          "Redesigned the weekly calendar view to clearly highlight today's column, making it easier to see your current day at a glance.",
      },
      {
        type: "improved",
        title: "Booth Plan Sort Order",
        description:
          "Booth rental plans now sort from least to most expensive across all views, helping barbers compare options more naturally.",
      },
    ],
  },
  {
    date: "February 6, 2026",
    items: [
      {
        type: "new",
        title: "Barber Schedule Management",
        description:
          "Barbers can now set and manage their weekly work schedules directly from the dashboard. Includes day-by-day time selection, break configuration, and a 'Copy to All' shortcut for quick setup.",
      },
      {
        type: "improved",
        title: "Shop Creation Image Uploads",
        description:
          "Fixed shop logo, cover image, and gallery photo uploads during shop creation. All images now properly persist and display across the entire platform.",
      },
      {
        type: "improved",
        title: "Payment & Checkout Reliability",
        description:
          "Improved payment request handling for barbers with in-app notifications. More reliable booth plan data fetching and seamless plan selection during onboarding.",
      },
      {
        type: "improved",
        title: "Saved Listings & UI Polish",
        description:
          "Modernized saved listings cards with a cleaner design. Improved time picker usability and polished schedule section layout and spacing.",
      },
    ],
  },
  {
    date: "February 2, 2026",
    items: [
      {
        type: "fixed",
        title: "Booth Payment Checkout",
        description:
          "Fixed issue where barbers couldn't complete payments when selecting a booth plan. The 'Continue to Payment' button now properly redirects to Stripe checkout.",
      },
      {
        type: "improved",
        title: "Barber Payments Page",
        description:
          "Redesigned the payment history page for barbers with a cleaner, simpler layout. Now shows transactions grouped by month with a summary of total spent.",
      },
    ],
  },
  {
    date: "January 28, 2026",
    items: [
      {
        type: "fixed",
        title: "Save Listing Feature",
        description:
          "Fixed the heart/save button on individual listing pages. Barbers can now save their favorite shops and view them later from their profile.",
      },
      {
        type: "improved",
        title: "Payment Activity Cards",
        description:
          "Redesigned payment activity section on barber dashboard with individual transaction cards showing shop logos, amounts, and dates in a cleaner format.",
      },
    ],
  },
  {
    date: "January 25, 2026",
    items: [
      {
        type: "improved",
        title: "Shop Listings Sort Order",
        description:
          "Shops now display with the most active barbers first, helping barbers find popular and established workspaces more easily.",
      },
      {
        type: "improved",
        title: "Owner Dashboard Layout",
        description:
          "Moved 'Add New Shop' card below existing shops for better flow. Your shops now take priority in the dashboard view.",
      },
      {
        type: "improved",
        title: "Request Payment Modal",
        description:
          "Added clear success and error states when shop owners request payments from barbers. Better feedback on what happened after clicking send.",
      },
    ],
  },
  {
    date: "January 23, 2026",
    items: [
      {
        type: "improved",
        title: "Owner Dashboard & Roster",
        description:
          "Improved manage shop links, roster member cards, and modal interactions for a smoother shop management experience.",
      },
      {
        type: "improved",
        title: "Shop Card Design",
        description:
          "Updated booth availability display and shop cards with cleaner visuals and better information hierarchy.",
      },
      {
        type: "new",
        title: "Full-Screen Photo Viewer",
        description:
          "Added lightbox gallery to shop listings. Click any photo to view it full-screen with easy navigation between images.",
      },
    ],
  },
  {
    date: "January 21, 2026",
    items: [
      {
        type: "improved",
        title: "Booth Rental Modal",
        description:
          "Cleaned up the plan selection modal with better price display, improved spacing, and clearer plan comparisons.",
      },
      {
        type: "improved",
        title: "Owner Dashboard Cards",
        description:
          "Added horizontal scrolling shop cards on owner dashboard for easier navigation when managing multiple locations.",
      },
    ],
  },
  {
    date: "January 20, 2026",
    items: [
      {
        type: "fixed",
        title: "Map Zoom Controls Visibility",
        description:
          "Relocated zoom controls from bottom-right to top-right corner of map to prevent clipping at viewport edge. Controls now fully visible on all screen sizes with improved responsive sizing.",
      },
      {
        type: "improved",
        title: "Owner Dashboard Welcome Section",
        description:
          "Updated Owner Dashboard welcome section spacing to match Barber Dashboard UI consistency. Increased vertical padding (py-8) and adjusted date margin (mb-2) for better visual hierarchy.",
      },
      {
        type: "fixed",
        title: "Phone Validation Regex",
        description:
          "Fixed syntax error in account settings phone number validation regex. Users can now properly save phone numbers containing digits, spaces, hyphens, plus signs, and parentheses.",
      },
    ],
  },
  {
    date: "January 19, 2026",
    items: [
      {
        type: "fixed",
        title: "Shop Detail Page Async Params",
        description:
          "Fixed Next.js 16 async params handling in shop detail pages (both owner and barber). Shop IDs now properly resolve before data fetching, preventing undefined errors.",
      },
      {
        type: "improved",
        title: "Shop Page Error Resilience",
        description:
          "Added comprehensive error handling for fetch cancellations and storage listeners. Shop pages now gracefully handle AbortError exceptions and continuous re-renders without crashing.",
      },
      {
        type: "fixed",
        title: "Stripe Connected Banner Dismiss",
        description:
          "Added X button to dismiss Stripe Connected banner on shop dashboard. Dismissal saves to localStorage and users can still access Stripe through Settings > Billing.",
      },
      {
        type: "fixed",
        title: "Stripe Dashboard Button",
        description:
          "Fixed 500 error on Stripe Dashboard button with improved API key validation and user-friendly error messages when Stripe is not properly configured.",
      },
      {
        type: "improved",
        title: "Role Switching Flow",
        description:
          "Simplified role switching to allow unrestricted mode toggling between Barber and Owner modes. Removed all validation restrictions for seamless user experience.",
      },
    ],
  },
  {
    date: "January 18, 2026",
    items: [
      {
        type: "fixed",
        title: "Release Barber Feature",
        description:
          "Fixed release barber function by removing reference to non-existent 'updated_at' column in shop_barbers table. Changed status from 'released' to 'inactive' and improved status to 'cancelled' for booth_rentals.",
      },
      {
        type: "improved",
        title: "Send Offer Modal Flow",
        description:
          "Fixed modal flashing during offer submission. Added smooth 300ms delay before closing and improved success toast with plan details. Button now shows 'Sending...' during submission.",
      },
      {
        type: "fixed",
        title: "Shop ID Undefined Navigation",
        description:
          "Added shop ID validation with proper error handling to prevent undefined ID errors. Shop cards now safely navigate with fallback to shop_id if shopData.id is missing.",
      },
      {
        type: "improved",
        title: "Booth Rental Query Errors",
        description:
          "Fixed 406 errors in booth_rentals queries by changing from .single() to .maybeSingle() throughout roster management. Prevents errors when no active rental records exist.",
      },
      {
        type: "improved",
        title: "Roster Management UI Feedback",
        description:
          "Enhanced release barber and send offer features with clearer success messages, loading states, and error handling. Users now get immediate visual feedback for all actions.",
      },
    ],
  },
  {
    date: "January 17, 2026",
    items: [
      {
        type: "fixed",
        title: "Payment Tracking System",
        description:
          "Fixed critical issue where booth rental payments weren't appearing in owner dashboards. All transactions now display correctly with complete payment history, earnings summaries, and barber payment details.",
      },
      {
        type: "fixed",
        title: "Stripe Webhook Integration",
        description:
          "Resolved payment processing bug that prevented transactions from being recorded. Shop owners now see real-time payment updates and accurate total earnings in their analytics.",
      },
      {
        type: "new",
        title: "Subscription Cancellation on Release",
        description:
          "When owners release a barber from their roster, their Stripe subscription now automatically cancels to stop future charges. Barbers receive an instant notification explaining the release.",
      },
      {
        type: "improved",
        title: "Multiple Payment Support",
        description:
          "Barbers can now make multiple payments to the same shop, allowing them to pay ahead for future months or add additional booth services whenever needed.",
      },
      {
        type: "fixed",
        title: "Back Button Spacing",
        description:
          "Fixed layout bug in account settings where the back arrow was too close to the sidebar. Improved spacing and touch targets for better mobile experience.",
      },
      {
        type: "improved",
        title: "Booth Options Display",
        description:
          "Redesigned booth availability stats on listing pages with cleaner inline layout and better responsive spacing for mobile and tablet devices.",
      },
      {
        type: "new",
        title: "White Favicon Design",
        description:
          "Updated app icon to professional white background with gray BOOTH emblem for better visibility in browser tabs and bookmarks across all devices.",
      },
    ],
  },
  {
    date: "January 16, 2026",
    items: [
      {
        type: "improved",
        title: "Payment Confirmation Flow",
        description:
          "Simplified subscription confirmation with clearer payment schedule details. Changed 'Auto-Renewal' to 'Auto-Pay' and made it crystal clear when recurring charges begin.",
      },
      {
        type: "fixed",
        title: "Gallery Photo Cutoff on Mobile",
        description:
          "Fixed booth listing gallery images being cut off at the top on mobile devices. Optimized carousel height for perfect visibility across all screen sizes.",
      },
{
        type: "improved",
        title: "Modal Overflow Fix",
        description:
          "Fixed plan selection modal appearing cut off with oval edges. Updated responsive sizing and border radius for clean display on all devices.",
      },
    ],
  },
  {
    date: "January 13, 2026",
    items: [
      {
        type: "improved",
        title: "Request Management Simplified",
        description:
          "Consolidated duplicate request sections in owner dashboard. Now shows 'New Barber Applications' and 'Existing Barber Requests' in one clear view.",
      },
      {
        type: "fixed",
        title: "Payment Button Functionality",
        description:
          "Fixed critical bug where payment completion buttons weren't responding to clicks. All payment options (weekly, monthly, cash) now work instantly.",
      },
      {
        type: "improved",
        title: "Owner Approval System",
        description:
          "Fixed approve/deny buttons in shop roster management. Owners can now approve or decline barber requests from both the shop page and notifications.",
      },
      {
        type: "fixed",
        title: "Barber Shop Navigation",
        description:
          "Fixed booth card navigation in barber dashboard. Clicking on 'My Booths' now properly navigates to the shop detail page with all rental information.",
      },
      {
        type: "improved",
        title: "Transaction Display Design",
        description:
          "Completely redesigned payment activity section with larger avatars, clearer transaction details, prominent green amounts, and better date formatting for easier tracking.",
      },
    ],
  },
  {
    date: "January 9, 2026",
    items: [
      {
        type: "fixed",
        title: "Barber Shop Loading Issue",
        description:
          "Fixed infinite loading state when barbers click on shop listings. Shop details now load instantly with all booth plan information displayed correctly.",
      },
      {
        type: "new",
        title: "Marketing Landing Page",
        description:
          "Launched dedicated /shops-flyer landing page for print marketing campaigns. Shop owners scanning QR codes see a conversion-optimized page highlighting key benefits and quick 3-step onboarding.",
      },
      {
        type: "improved",
        title: "Barber Mode & Owner Mode Switching",
        description:
          "Redesigned mode switcher to be fully interactive and easily accessible. Users can now toggle between Owner and Barber modes directly from account settings with one click.",
      },
      {
        type: "fixed",
        title: "Shop Display in Account Settings",
        description:
          "Fixed bug where shop owners weren't seeing their shop listings in the Billing tab. Shop data now correctly displays based on current view mode, not just account role.",
      },
      {
        type: "improved",
        title: "Professional Footer Design",
        description:
          "Added comprehensive footer across all marketing pages with brand information, quick links for both shop owners and barbers, support resources, and legal links.",
      },
    ],
  },
  {
    date: "January 8, 2026",
    items: [
      {
        type: "fixed",
        title: "Payment & Transaction System",
        description:
          "Fixed critical payment flow to ensure all booth rental payments are properly recorded and displayed. Shop owners now see accurate transaction history in their analytics dashboard.",
      },
      {
        type: "improved",
        title: "Barber Onboarding & Reservations",
        description:
          "Fixed 'No Plans Available' error when barbers click Reserve. Booth rental plans now display correctly and barbers can complete the full reservation flow seamlessly.",
      },
      {
        type: "new",
        title: "Platform Fee & Coupon Codes",
        description:
          "Added transparent 2% platform fee for barbers (shop owners receive 100% of booth rent). Barbers can enter coupon codes to waive the platform fee for promotional periods.",
      },
      {
        type: "fixed",
        title: "Mobile Layout Improvements",
        description:
          "Fixed off-center mobile layout on booth listing pages. All modals and content now properly fit the viewport with centered alignment on all devices.",
      },
      {
        type: "improved",
        title: "Account Settings & Mode Switching",
        description:
          "Fixed shop display in billing settings for owners and made the Owner/Barber mode switcher fully interactive. Users can now easily toggle between modes with one click.",
      },
      {
        type: "fixed",
        title: "Email Verification & Redirects",
        description:
          "Fixed authentication redirect issue where users were being sent to preview environment instead of production site after email verification. All auth flows now properly route to boothapp.co.",
      },
    ],
  },
  {
    date: "January 7, 2026",
    items: [
      {
        type: "new",
        title: "Guest Booth Invitations",
        description:
          "Shop owners can now invite barbers as guests at $0 cost. Guests join the roster immediately without payment, and owners can send paid plan offers later.",
      },
      {
        type: "new",
        title: "Cash Barber Option",
        description:
          "Owners can onboard barbers with custom pricing but collect payment outside the platform (cash, Venmo, Zelle). Perfect for shops that prefer traditional payment methods.",
      },
      {
        type: "fixed",
        title: "Mobile Dashboard Layout",
        description:
          "Fixed critical issue where content was cut off behind the navbar on mobile across all dashboard pages. Back buttons, headers, and save buttons now fully visible.",
      },
      {
        type: "fixed",
        title: "Barber-Owner Messaging",
        description:
          "Fixed bug preventing barbers from contacting shop owners. The 'Contact Shop' button now works correctly with proper owner identification.",
      },
      {
        type: "improved",
        title: "Design System Consistency",
        description:
          "Standardized all buttons to rounded-full styling. Removed navbar scroll jank for buttery-smooth performance across the entire platform.",
      },
      {
        type: "fixed",
        title: "Logo Upload on Mobile",
        description:
          "Removed forced camera capture. Users can now choose between taking a photo or selecting from gallery on mobile devices.",
      },
    ],
  },
  {
    date: "January 6, 2026",
    items: [
      {
        type: "new",
        title: "Gallery Photos for Shops",
        description:
          "Shop owners can now upload up to 6 additional photos when creating their shop, giving barbers a better view of the space with drag-and-drop support and instant previews.",
      },
      {
        type: "new",
        title: "Custom Amenities",
        description:
          "Create your own custom amenities when listing a shop. Add unique features like 'Espresso Machine' or 'Massage Chair' that aren't in our preset list.",
      },
      {
        type: "fixed",
        title: "Mobile Image Uploads",
        description:
          "Fixed issue where uploaded shop logos and photos weren't displaying on mobile devices during shop creation.",
      },
      {
        type: "fixed",
        title: "Release Barber Feature",
        description:
          "Owners can now properly release barbers from their roster. Released barbers immediately disappear from the team list as expected.",
      },
      {
        type: "fixed",
        title: "Profile Image Display",
        description:
          "Fixed stretched and distorted profile photos throughout the app. All avatars now display correctly with proper cropping and centering.",
      },
    ],
  },
  {
    date: "January 5, 2026",
    items: [
      {
        type: "fixed",
        title: "Critical Dashboard Errors Resolved",
        description:
          "Fixed multiple critical errors preventing barbers and owners from accessing their dashboards. Corrected router imports and authentication flows across the platform.",
      },
      {
        type: "fixed",
        title: "Profile Images Display",
        description:
          "Resolved stretched and off-center profile images across both barber and owner modes by adding proper object-cover styling to all avatar components.",
      },
      {
        type: "improved",
        title: "Release Barber Functionality",
        description:
          "Enhanced roster management with proper authentication, ownership verification, and immediate UI refresh when releasing barbers from shops.",
      },
      {
        type: "new",
        title: "Custom Amenities Support",
        description:
          "Shop owners can now add custom amenities beyond the preset list when creating or editing their shops (e.g., 'Espresso Machine', 'Shoe Shine Station').",
      },
      {
        type: "new",
        title: "Gallery Upload for Shops",
        description:
          "Added ability to upload up to 6 additional shop photos during shop creation with drag-and-drop support and preview thumbnails.",
      },
    ],
  },
]

const typeStyles = {
  new: {
    badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    label: "New",
  },
  improved: {
    badge: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    label: "Improved",
  },
  fixed: {
    badge: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    label: "Fixed",
  },
  removed: {
    badge: "bg-neutral-500/10 text-neutral-400 border-neutral-500/20",
    label: "Removed",
  },
}

export default function ChangelogPage() {
  const t = useTranslations("pages.changelog")
  const locale = useLocale()
  const [activeTab, setActiveTab] = useState<"changelog" | "journal">("changelog")
  const [adminMode, setAdminMode] = useState(false)
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false)
  const [password, setPassword] = useState("")
  const [deletedItems, setDeletedItems] = useState<Set<string>>(new Set())
  const [expandedDates, setExpandedDates] = useState<Set<number>>(new Set())

  const toggleDate = (index: number) => {
    setExpandedDates((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  // The auth lookup that used to live here is gone with the bespoke header.
  // It resolved the user and their role purely to pick a Dashboard link — two
  // Supabase round-trips on every changelog load, for a link PublicNavbar now
  // renders from state it already holds.

  const handleTitleClick = () => {
    setShowPasswordPrompt(true)
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === "BOOTH26") {
      setAdminMode(true)
      setShowPasswordPrompt(false)
      setPassword("")
    } else {
      alert(t("admin.incorrectPassword"))
      setPassword("")
    }
  }

  const handleDelete = (updateIndex: number, itemIndex: number) => {
    const key = `${updateIndex}-${itemIndex}`
    setDeletedItems((prev) => new Set([...prev, key]))
  }

  const isDeleted = (updateIndex: number, itemIndex: number) => {
    return deletedItems.has(`${updateIndex}-${itemIndex}`)
  }

  return (
    <div data-nav-theme="dark" className="min-h-screen bg-neutral-950 text-white">
      {/* Header — hidden below md since FloatingNav covers mobile nav */}
      {/* Same chrome as every other public page. This used to roll its own
          header — a back arrow and a logo hotlinked from an old Webflow CDN,
          plus a hand-built Browse / Community / Dashboard row that drifted
          from the real nav. /traction made this same move already; this is
          the last page still carrying a bespoke one. */}
      <PublicNavGlass defaultMode="dark" showModeToggle={false} />

      {/* pt-28 on mobile: the floating nav ends ~68px down and pt-20 sat the
          eyebrow right under it. */}
      <main className="max-w-3xl mx-auto px-6 pt-28 pb-12 sm:pt-20">
        {/* Title */}
        <div className="mb-12">
          <div className="flex flex-col items-center text-center md:items-start md:text-left">
            <p className="text-neutral-500 text-sm tracking-widest uppercase mb-3 flex items-center gap-2">
              <PulseDot />
              {t("header.badge")}
            </p>
            <h1
              className="text-4xl font-light tracking-tight cursor-default select-none md:text-4xl"
              onClick={handleTitleClick}
            >
              {t("header.title")}
            </h1>
            <p className="text-neutral-500 mt-4 text-lg max-w-2xl">
              {t("header.subtitle")}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-6 mt-8 border-b border-neutral-800">
            <button
              onClick={() => setActiveTab("changelog")}
              className={`pb-3 text-sm transition-colors relative ${
                activeTab === "changelog"
                  ? "text-white"
                  : "text-neutral-500 hover:text-neutral-300"
              }`}
            >
              {t("tabs.changelog")}
              {activeTab === "changelog" && (
                <span className="absolute bottom-0 left-0 right-0 h-px bg-white" />
              )}
            </button>
            {/* Founder's Journal tab hidden for now — will return later */}
          </div>
          {adminMode && (
            <div className="mt-4 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
              {t("admin.banner")}
            </div>
          )}
          {/* Password Prompt Modal */}
          {showPasswordPrompt && !adminMode && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
              <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 max-w-sm w-full mx-4">
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="w-5 h-5 text-neutral-400" />
                  <h3 className="text-lg font-normal">{t("admin.title")}</h3>
                </div>
                <form onSubmit={handlePasswordSubmit}>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("admin.passwordPlaceholder")}
                    autoFocus
                    className="w-full px-4 py-2 bg-neutral-950 border border-neutral-800 rounded-lg text-white placeholder:text-neutral-600 focus:outline-none focus:border-neutral-700 mb-4"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordPrompt(false)
                        setPassword("")
                      }}
                      className="flex-1 px-4 py-2 bg-neutral-800 hover:bg-neutral-750 rounded-lg text-sm transition-colors"
                    >
                      {t("admin.cancel")}
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-white text-black hover:bg-neutral-200 rounded-lg text-sm transition-colors"
                    >
                      {t("admin.unlock")}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Journal Tab */}
        {activeTab === "journal" && (
          <div className="space-y-10">
            {journalEntries.map((entry, index) => (
              <div key={index} className="border-b border-neutral-900 pb-16 last:border-0">
                <div className="text-2xl font-light tracking-tight mb-4">{formatEntryDate(entry.date, locale)}</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {entry.tags.map((tag) => {
                    const config = tagConfig[tag]
                    if (!config) return null
                    const TagIcon = config.icon
                    return (
                      <span
                        key={tag}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs ${config.color}`}
                      >
                        <TagIcon className="w-3 h-3" />
                        {t(`tags.${tag}`)}
                      </span>
                    )
                  })}
                </div>
                <p className="text-neutral-400 text-xs leading-relaxed whitespace-pre-line pl-4 border-l border-neutral-800">{entry.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* Changelog Tab */}
        {activeTab === "changelog" && (
          <div className="space-y-10">
            {updates.map((update, updateIndex) => {
              const collapsible = updateIndex >= 1
              const isOpen = !collapsible || expandedDates.has(updateIndex)
              const visibleCount = update.items.filter(
                (_, i) => !isDeleted(updateIndex, i),
              ).length

              return (
              <div
                key={updateIndex}
                className={`border-b border-neutral-900 last:border-0 ${
                  collapsible && !isOpen ? "pb-6" : "pb-16"
                }`}
              >
                {/* Date & Version */}
                {collapsible ? (
                  <button
                    type="button"
                    onClick={() => toggleDate(updateIndex)}
                    aria-expanded={isOpen}
                    className={`group flex w-full items-center justify-between gap-4 text-left transition-colors hover:text-white ${
                      isOpen ? "mb-8" : "mb-0"
                    }`}
                  >
                    <div className="flex items-baseline gap-3">
                      <div className="text-2xl font-light tracking-tight">{formatEntryDate(update.date, locale)}</div>
                      {!isOpen && (
                        <span className="text-xs uppercase tracking-wider font-medium text-yellow-300">
                          {t("updatesCount", { count: visibleCount })}
                        </span>
                      )}
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-neutral-500 transition-transform group-hover:text-neutral-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                ) : (
                  <div className="flex items-center gap-4 mb-8">
                    <div className="text-2xl font-light tracking-tight">{formatEntryDate(update.date, locale)}</div>
                  </div>
                )}

                {/* Items */}
                {isOpen && (
                <div className="space-y-6">
                  {update.items.map((item, itemIndex) => {
                    if (isDeleted(updateIndex, itemIndex)) return null

                    const style = typeStyles[item.type as keyof typeof typeStyles]
                    return (
                      <div key={itemIndex} className="group">
                        <div className="flex items-start gap-3">
                          <div
                            className={`px-2 py-1 rounded border text-xs tracking-wider uppercase mt-0.5 ${style.badge}`}
                          >
                            {t(`badges.${item.type}`)}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-normal mb-1 text-white group-hover:text-neutral-300 transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-neutral-500 text-sm leading-relaxed">{item.description}</p>
                          </div>
                          {adminMode && (
                            <button
                              onClick={() => handleDelete(updateIndex, itemIndex)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-red-500/10 rounded-lg text-red-400 hover:text-red-300"
                              title={t("admin.deleteTooltip")}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
                )}
              </div>
              )
            })}
          </div>
        )}

        {/* Footer CTA */}
        <div className="pt-6 mt-12 border-t border-neutral-900">
          <p className="text-neutral-500 text-sm mb-4">{t("cta.question")}</p>
          <Link
            href="/auth/signup"
            className="inline-block text-sm tracking-widest uppercase text-neutral-500 hover:text-white transition-colors"
          >
            {t("cta.join")}
          </Link>
        </div>
      </main>
    </div>
  )
}
